import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, Navigate, Outlet, Route, Routes, matchPath, useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useDeepLinkScroll } from "../hooks/useDeepLinkScroll";
import { AlertTriangle, Check, ChevronRight, Download, FileText, Image as ImageIcon, Paperclip, UserPlus, X } from "lucide-react";
import { theme, card, countTag, emptyFrame, floatingSheet, btnPrimary, btnSecondary, inputStyle } from "../theme";
import { DimensionHeading, RulerProgress, StatusTag, type StatusTone } from "../components/Drawing";
import { SignInPolicyPanel } from "../components/SignInPolicyPanel";
import {
  frameworkAccent, displayRef,
  ControlStatusBadge, DocCountChip, PendingApprovalChip,
  ControlSectionHeader, ControlSectionBody,
} from "../components/controlStatus";
import { api, ApiError, apiErrorMessage } from "../api/client";
import { fmtDate, fmtBytes } from "../utils/format";
import { geographyLabel } from "../lib/geography";
import { Spinner, LoadingBlock, ErrorBlock } from "../components/Spinner";
import { WhatsNewPage } from "./WhatsNew";
import { ActionsTile } from "../components/ActionsTile";
import { BureauConnectorsView } from "../components/BureauConnectorsView";
import { InviteHistoryTable } from "../components/InviteHistoryTable";
import type { InviteSummary } from "../components/InviteHistoryTable";
import type { CurrentUser } from "../auth/useCurrentUser";
import { AppShell } from "../nav/AppShell";
import { CONSULTANT_HOME } from "../nav/consultantNav";
import { Breadcrumb } from "../nav/AppTopBar";
import { useRouterLinkAdapter } from "../nav/LinkAdapter";
import { AUDIT_TAB_LABELS, AUDITOR_HOME, auditClientPath, buildAuditorNav } from "../nav/auditorNav";
import type { AuditorNavClient, AuditTab } from "../nav/auditorNav";
import { FW_LABEL } from "../nav/frameworkMeta";
import { CERTIFIABLE_FRAMEWORKS, CE_FRAMEWORK_IDS, type CertifiableFrameworkId } from "../context/FrameworksContext";
import type { NavModel, PaletteEntry, WorkspaceSwitch } from "../nav/types";
import { AuditorClientsContext, AUDIT_TYPE_LABELS, AUDITOR_ROLE_LABELS, BUREAU_ADMIN_NOTE, CONSULTANT_ROLE_LABELS, useAuditorClients } from "./auditor/auditorShared";
import type { AuditorClientListResponse, AuditorClientSummary, AuditorClientsState, BureauSummary } from "./auditor/auditorShared";
import { AuditorCalendarPage } from "./auditor/AuditorCalendarPage";
import { AuditorRegistersTab } from "./auditor/AuditorRegistersTab";
import { AuditorInvitationsPage, resendBureauInvite, revokeBureauInvite } from "./auditor/AuditorInvitationsPage";
import { toDateInput, dateInputToIso, fmtDateOnly, todayInput } from "../lib/dateOnly";
import { displayControlRef, displayControlTitle } from "../lib/controlRef";
import { BureauChangeRequestPanel } from "../components/BureauChangeRequestPanel";
import { EvidenceTrailDrawer } from "./auditor/EvidenceTrailDrawer";
import { AuditSnapshotsTab } from "./auditor/AuditSnapshotsTab";
import { AuditSamplingTab } from "./auditor/AuditSamplingTab";
import { FindingActionStatus, type FindingActionSummary } from "../components/FindingCorrectiveAction";

import { cbSafe } from "../lib/colourBlind";
// ── Constants ─────────────────────────────────────────────────────────────

// One label set for every role — the auditor names a standard exactly as the
// client and consultant see it (nav/frameworkMeta.ts is the single source).
const FRAMEWORK_LABELS: Record<string, string> = FW_LABEL;

// ── Types ─────────────────────────────────────────────────────────────────
// The client-list shapes (AuditorClientSummary, BureauSummary) live in
// ./auditor/auditorShared.ts because the shell, the calendar and the client
// list all read the same GET /auditor/clients rows.

interface AuditorFinding {
  id: string;
  controlId: string;
  text: string;
  status: "open" | "acknowledged" | "resolved";
  // The client's own progress — "resolved" is a request for verification, not an approval.
  clientStatus?: string | null;
  clientStatusAt?: string | null;
  createdAt: string;
  // The client's corrective action for it, and whether closing it has been proposed (lib/findingActions.ts).
  correctiveAction?: FindingActionSummary | null;
  closureProposed?: boolean;
}

interface EvidenceRequestItem {
  id: string;
  controlId: string;
  frameworkId: string | null;
  message: string;
  status: string; // pending | acknowledged | fulfilled | withdrawn
  createdAt: string;
}

interface FindingCommentItem {
  id: string;
  text: string;
  authorRole: string;
  createdAt: string;
  author: { displayName: string | null; email: string };
}

interface ClauseReview {
  controlId: string;
  status: string | null;
  notes: string | null;
  updatedAt?: string;
}

interface SoaEntry {
  controlId: string;
  status: string;
  applicable: boolean | null;
  justification: string | null;
  aiNote: string | null;
  approvalStatus: string;
  updatedAt?: string;
}

interface EvidenceDoc {
  id: string;
  fileName: string;
  sizeBytes: number;
  uploadedAt: string;
}

interface LinkedRisk {
  id: string;
  title: string;
  status: string;
  residualScore: number | null;
}

interface ControlComment {
  id: string;
  controlId: string;
  body: string;
  createdAt: string;
  user: { displayName: string | null; role: string };
}

interface ControlWithContext {
  id: string;
  frameworkId: string;
  kind: "CLAUSE" | "CONTROL";
  domain: string | null;
  name: string;
  description: string;
  requiredEvidence: string[];
  soaEntry: SoaEntry | null;
  clauseReview: ClauseReview | null;
  auditorFinding: AuditorFinding | null;
  documents: EvidenceDoc[];
  risks: LinkedRisk[];
  comments: ControlComment[];
  /** Cyber Essentials: where the platform's records contradict the client's answer (backend lib/ceConsistency.ts). */
  recordsDisagree?: string[];
}

interface AuditorClientDetail {
  id: string;
  name: string;
  /** False when the client's edition leaves CIS hardening out (the Jersey edition). */
  cisHardening?: boolean;
  sector: string | null;
  geography: string | null;
  ismsScope: string | null;
  bureau: { id: string; name: string; accreditation: string | null } | null;
  serviceTier: { id: string; level: number; name: string };
  scopedFrameworkIds?: string[];
  latestCheck: {
    id: string;
    overallScore: number | null;
    resultJson: unknown;
    completedAt: string | null;
  } | null;
  controls: ControlWithContext[];
}


// resultJson shape (AI check output)
interface AiControlResult {
  controlId: string;
  evidenceFound?: string[];
  gaps?: string[];
  recommendations?: string[];
}
interface AiResultJson {
  perControl?: AiControlResult[];
}

// ── Helpers ───────────────────────────────────────────────────────────────

type FindingStatus = "open" | "acknowledged" | "resolved";
type StatusFilter = FindingStatus | "all" | "none";

function scoreColor(score: number | null): string {
  if (score === null) return theme.color.faint;
  if (score >= 80) return theme.color.success;
  if (score >= 50) return theme.color.warning;
  return theme.color.danger;
}

function findingTone(status: FindingStatus | null): StatusTone {
  if (!status) return "none";
  if (status === "resolved") return "met";
  if (status === "acknowledged") return "partial";
  return "gap";
}

function statusLabel(status: FindingStatus | null): string {
  if (!status) return "No finding";
  if (status === "resolved") return "Approved";
  if (status === "acknowledged") return "Observation";
  return "Needs Work";
}

// ── Status badge ──────────────────────────────────────────────────────────

function Badge({ label, color, bg }: { label: string; color: string; bg: string }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 3,
        fontSize: 11,
        fontWeight: 600,
        color,
        background: bg,
      }}
    >
      {label}
    </span>
  );
}

// ── Client list view ──────────────────────────────────────────────────────

function ClientListView({ user }: { user: CurrentUser }) {
  const navigate = useNavigate();
  const { clients, loading, error, reload } = useAuditorClients();

  // The shell loads the list once; coming back here picks up new findings,
  // verifications and schedules without a full refresh.
  useEffect(() => { reload(); }, [reload]);

  const specialistScope = user.auditorRole === "framework_specialist" ? (user.frameworkAccess ?? []) : null;

  if (loading && clients.length === 0) return <LoadingBlock label="Loading clients" />;
  if (error && clients.length === 0) return <ErrorBlock message={error} onRetry={reload} />;

  return (
    <div className="page-pad">
      <div>
        {/* Actions tile */}
        <div style={{ marginBottom: 20 }}>
          <ActionsTile endpoint="/auditor/actions" onClick={() => navigate("/auditor/actions")} />
        </div>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: specialistScope ? 8 : 24, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.color.heading }}>
            Your Assigned Clients
          </h2>
          <span style={countTag}>{clients.length}</span>
        </div>

        {specialistScope && (
          <div style={{ fontSize: 12, color: specialistScope.length > 0 ? theme.color.subtle : theme.color.warningText, marginBottom: 20, lineHeight: 1.5 }}>
            {specialistScope.length > 0
              ? `Specialist scope: ${specialistScope.map((fw) => FRAMEWORK_LABELS[fw] ?? fw).join(", ")}`
              : "Specialist scope: no standards granted yet. Ask a bureau administrator to grant you at least one standard in Team & Access."}
          </div>
        )}

        {clients.length === 0 ? (
          <div style={{ ...emptyFrame, color: theme.color.muted, fontSize: 13, padding: 48 }}>
            No clients have selected your bureau yet.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(min(320px, 100%), 1fr))",
              gap: 16,
            }}
          >
            {clients.map((c) => (
              <ClientCard key={c.id} client={c} onClick={() => navigate(auditClientPath(c.id))} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ClientCard({ client, onClick }: { client: AuditorClientSummary; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  const score = client.latestCheck?.overallScore ?? null;
  const color = scoreColor(score);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...card,
        cursor: "pointer",
        border: `1px solid ${hovered ? theme.color.accent : "var(--c-line)"}`,
        transition: "border-color 0.15s",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.color.heading }}>{client.name}</div>
          <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>
            {[client.sector, geographyLabel(client.geography)].filter(Boolean).join(" · ") || "No profile details"}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
            {client.frameworkIds.length === 0 ? (
              <span style={{ fontSize: 11, color: theme.color.faint, fontStyle: "italic" }}>
                No enrolled standards within your accreditation
              </span>
            ) : client.frameworkIds.map((fw) => (
              <span
                key={fw}
                style={{
                  fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 4,
                  background: theme.color.accentBg, color: theme.color.accentText,
                  border: `1px solid ${theme.color.accent}`,
                }}
              >
                {FRAMEWORK_LABELS[fw] ?? fw}
              </span>
            ))}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 16 }}>
          {score !== null ? (
            <>
              <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1 }}>{score}%</div>
              <div style={{ fontSize: 10, color: theme.color.subtle, marginTop: 2 }}>
                {score >= 80 ? "Ready for audit" : score >= 50 ? "In progress" : "Needs attention"}
              </div>
            </>
          ) : client.latestCheck ? (
            // The latest run also assessed standards outside this auditor's scope,
            // so its overall score is withheld (the same rule as the audit view).
            <div style={{ fontSize: 11, color: theme.color.faint, fontStyle: "italic", maxWidth: 130 }}>
              Score not available for your audit scope
            </div>
          ) : (
            <div style={{ fontSize: 13, color: theme.color.faint, fontStyle: "italic" }}>No check run</div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <Badge
          label={`${client.clauseReviewCount} clause${client.clauseReviewCount !== 1 ? "s" : ""} approved`}
          color={client.clauseReviewCount > 0 ? theme.color.success : theme.color.faint}
          bg={client.clauseReviewCount > 0 ? theme.color.successBg : theme.color.panel}
        />
        {client.awaitingVerificationCount > 0 && (
          <Badge
            label={`${client.awaitingVerificationCount} to verify`}
            color={theme.color.accentText}
            bg={theme.color.accentBg}
          />
        )}
        {client.openFindingsCount > 0 && (
          <Badge
            label={`${client.openFindingsCount} open finding${client.openFindingsCount !== 1 ? "s" : ""}`}
            color={theme.color.dangerText}
            bg={theme.color.dangerBg}
          />
        )}
        {client.resolvedFindingsCount > 0 && client.openFindingsCount === 0 && (
          <Badge
            label={`${client.resolvedFindingsCount} finding${client.resolvedFindingsCount !== 1 ? "s" : ""} resolved`}
            color={theme.color.success}
            bg={theme.color.successBg}
          />
        )}
        <span style={{ fontSize: 11, color: theme.color.faint }}>
          {client.serviceTier.name}
        </span>
        {client.schedule?.isOverdue && (
          <Badge
            label={`${AUDIT_TYPE_LABELS[client.schedule.auditType] ?? client.schedule.auditType} overdue`}
            color={theme.color.dangerText}
            bg={theme.color.dangerBg}
          />
        )}
        {client.schedule && !client.schedule.isOverdue && (() => {
          const days = Math.ceil((new Date(client.schedule.scheduledDate).getTime() - Date.now()) / 86_400_000);
          return days <= 30 ? (
            <Badge label={`Audit in ${days}d`} color={theme.color.warningText} bg={theme.color.warningBg} />
          ) : null;
        })()}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          style={{ ...btnPrimary, fontSize: 12, padding: "6px 14px" }}
          onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
          Review →
        </button>
      </div>
    </div>
  );
}

// ── SOA status normaliser (CE+ uses lowercase, SOA uses uppercase) ────────

function normSoaStatus(s: string | undefined | null): string {
  if (!s) return "";
  const u = s.toUpperCase();
  if (u === "COMPLIANT" || u === "AI_COMPLIANT") return "COMPLIANT";
  if (u === "IN_PROGRESS") return "IN_PROGRESS";
  if (u === "GAP") return "GAP";
  return u;
}

// ── Evidence view: self-assessment summary ────────────────────────────────
//
// The same bar the client sees at the top of every framework page
// (FrameworkControlList), fed from the auditor's live SoA snapshot.

function SoaSummaryBar({ controls }: { controls: ControlWithContext[] }) {
  const total       = controls.length;
  const compliant   = controls.filter((c) => normSoaStatus(c.soaEntry?.status) === "COMPLIANT").length;
  const inProgress  = controls.filter((c) => normSoaStatus(c.soaEntry?.status) === "IN_PROGRESS").length;
  const gap         = controls.filter((c) => normSoaStatus(c.soaEntry?.status) === "GAP").length;
  const notAssessed = total - compliant - inProgress - gap;
  const pct         = total > 0 ? Math.round((compliant / total) * 100) : 0;
  const allDone     = compliant === total && total > 0;

  return (
    <div style={{ ...card, padding: "16px 20px", display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--c-faint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Controls compliant</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: allDone ? cbSafe("#22c55e") : "var(--c-accentText)" }}>{compliant} / {total}</div>
      </div>
      <div style={{ flex: 1, minWidth: 120 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: "var(--c-faint)" }}>Progress</span>
          <span style={{ fontSize: 11, color: "var(--c-faint)" }}>{pct}%</span>
        </div>
        <RulerProgress value={compliant} max={total} fill={total > 0 && compliant === total ? "var(--c-success)" : undefined} label="Controls compliant" />
      </div>
      <div style={{ display: "flex", gap: 24 }}>
        {[
          { count: notAssessed, label: "Not assessed",   color: "var(--c-subtle)" },
          { count: inProgress,  label: "In progress",    color: "var(--c-warning)" },
          { count: gap,         label: "Gap identified", color: "var(--c-danger)" },
          { count: compliant,   label: "Compliant",      color: "var(--c-successText)", check: true },
        ].map(({ count, label, color, check }) => (
          <div key={label} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 700, color }}>{count}</div>
            <div style={{ fontSize: 11, color: "var(--c-faint)", display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}>
              {check && <Check size={12} aria-hidden="true" />}
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Filter pill — the client's status-filter button.
function FilterPill({ active, onClick, children, small }: { active: boolean; onClick: () => void; children: React.ReactNode; small?: boolean }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: small ? "5px 10px" : "6px 12px", borderRadius: small ? 3 : 3, fontSize: small ? 11 : 12, fontWeight: 600, cursor: "pointer",
        border: `1px solid ${active ? theme.color.accent : theme.color.borderStrong}`,
        background: active ? theme.color.accentBg : theme.color.panel,
        color: active ? theme.color.accentText : theme.color.subtle,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

// A framework's controls grouped the way the client's pages lay them out:
// mandatory clauses first, then one section per Annex / domain.
interface ControlSection { key: string; title: string; controls: ControlWithContext[] }

function sectionsFor(controls: ControlWithContext[]): ControlSection[] {
  const out: ControlSection[] = [];
  const clauses = controls.filter((c) => c.kind === "CLAUSE");
  if (clauses.length) out.push({ key: "clauses", title: "Mandatory clauses", controls: clauses });
  const byDomain = new Map<string, ControlWithContext[]>();
  const undomained: ControlWithContext[] = [];
  for (const c of controls) {
    if (c.kind !== "CONTROL") continue;
    if (!c.domain) { undomained.push(c); continue; }
    const list = byDomain.get(c.domain) ?? [];
    list.push(c);
    byDomain.set(c.domain, list);
  }
  for (const [domain, list] of byDomain) out.push({ key: `domain:${domain}`, title: domain, controls: list });
  if (undomained.length) out.push({ key: "controls", title: "Supplementary controls", controls: undomained });
  return out;
}

// ── Document viewer modal ─────────────────────────────────────────────────

function DocViewerModal({ fileName, url, onClose }: { fileName: string; url: string; onClose: () => void }) {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  const isImage = ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext);
  const isPdf   = ext === "pdf";
  const canPreview = isImage || isPdf;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        zIndex: 2000, padding: 20,
      }}
    >
      <div style={{ ...floatingSheet, width: "100%", maxWidth: 900, display: "flex", flexDirection: "column", maxHeight: "90vh", overflow: "hidden" }}>
        <div style={{
          background: theme.color.panel,
          borderBottom: "1px solid var(--c-line)",
          padding: "10px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{fileName}</span>
          <button
            onClick={onClose}
            style={{
              background: "none", border: `1px solid ${theme.color.border}`,
              borderRadius: 3, padding: "4px 12px", fontSize: 12,
              color: theme.color.muted, cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
        <div style={{
          flex: 1, overflow: "hidden",
          background: theme.color.bg,
          display: "flex", alignItems: "center", justifyContent: "center",
          minHeight: 400,
        }}>
          {isPdf && (
            <iframe src={url} title={fileName} style={{ width: "100%", height: "75vh", border: "none", display: "block" }} />
          )}
          {isImage && (
            <img src={url} alt={fileName} style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", display: "block" }} />
          )}
          {!canPreview && (
            <div style={{ textAlign: "center", padding: 40, color: theme.color.subtle, fontSize: 13 }}>
              <FileText size={32} style={{ marginBottom: 12, color: theme.color.subtle }} />
              <div style={{ marginBottom: 8 }}>Preview not available for .{ext} files.</div>
              <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: theme.color.accentText, fontSize: 12 }}>
                Open in new tab
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Evidence view: clause card ────────────────────────────────────────────

/** A row that can take a finding: any control, or a mandatory clause the consultant has approved. */
function bulkEligible(c: ControlWithContext): boolean {
  return c.kind === "CONTROL" || c.clauseReview?.status === "approved";
}

function ClauseCard({
  control,
  orgId,
  resultJson,
  onFindingUpdated,
  onFindingDeleted,
  bulkMode,
  bulkSelected,
  onBulkToggle,
  readOnly = false,
}: {
  control: ControlWithContext;
  orgId: string;
  readOnly?: boolean;
  resultJson: unknown;
  onFindingUpdated: (controlId: string, finding: AuditorFinding) => void;
  onFindingDeleted: (controlId: string) => void;
  bulkMode?: boolean;
  bulkSelected?: Set<string>;
  onBulkToggle?: (controlId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [draftText, setDraftText] = useState(control.auditorFinding?.text ?? "");
  const [draftStatus, setDraftStatus] = useState<FindingStatus>(control.auditorFinding?.status ?? "resolved");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [viewDoc, setViewDoc] = useState<{ id: string; fileName: string; url: string } | null>(null);
  const [viewLoading, setViewLoading] = useState<string | null>(null);
  const [viewError, setViewError] = useState<Record<string, string>>({});
  const [trailOpen, setTrailOpen] = useState(false);

  // Item 11 — finding comments
  const [comments, setComments] = useState<FindingCommentItem[]>([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);

  const loadComments = useCallback(async (findingId: string) => {
    if (commentsLoaded) return;
    try {
      const d = await api.get<{ comments: FindingCommentItem[] }>(
        `/auditor/clients/${orgId}/findings/${findingId}/comments`
      );
      setComments(d.comments);
      setCommentsLoaded(true);
    } catch { setSaveError("Failed to load comments."); }
  }, [orgId, commentsLoaded]);

  useEffect(() => {
    if (expanded && control.auditorFinding) {
      loadComments(control.auditorFinding.id);
    }
  }, [expanded, control.auditorFinding, loadComments]);

  const handlePostComment = async () => {
    if (!commentDraft.trim() || !control.auditorFinding) return;
    setCommentSaving(true);
    try {
      const c = await api.post<FindingCommentItem>(
        `/auditor/clients/${orgId}/findings/${control.auditorFinding.id}/comments`,
        { text: commentDraft.trim() }
      );
      setComments((prev) => [...prev, c]);
      setCommentDraft("");
    } catch { setSaveError("Failed to post comment. Please try again."); } finally {
      setCommentSaving(false);
    }
  };

  const findingStatus = control.auditorFinding?.status ?? null;
  const isConsultantApproved = control.clauseReview?.status === "approved";
  // Annex A controls have no consultant-review workflow — the form is always accessible
  const formUnlocked = control.kind === "CONTROL" || isConsultantApproved;

  const consultantChip: { tone: StatusTone; label: string } = isConsultantApproved
    ? { tone: "met", label: "Consultant: Approved" }
    : control.clauseReview?.status === "needs_work"
    ? { tone: "partial", label: "Consultant: Needs work" }
    : { tone: "none", label: "Consultant: Pending" };

  // AI result for this control
  let aiControl: AiControlResult | null = null;
  try {
    const rj = resultJson as AiResultJson;
    // The model sometimes prefixes ISO 27001 ids with "iso27001-"; the worker
    // strips it when it writes the SOA back, so match either spelling here.
    aiControl = rj?.perControl?.find((c) => c.controlId === control.id || c.controlId === `iso27001-${control.id}`) ?? null;
  } catch {
    // ignore
  }

  // Needs work and Observation need words the client can act on; an approval does not (the API agrees).
  const needsText = draftStatus !== "resolved" && !draftText.trim();

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    try {
      let result: AuditorFinding;
      if (control.auditorFinding) {
        result = await api.patch<AuditorFinding>(
          `/auditor/clients/${orgId}/findings/${control.auditorFinding.id}`,
          { text: draftText, status: draftStatus },
        );
      } else {
        result = await api.post<AuditorFinding>(`/auditor/clients/${orgId}/findings`, {
          controlId: control.id,
          text: draftText,
          status: draftStatus,
        });
      }
      onFindingUpdated(control.id, result);
    } catch (e) {
      setSaveError(apiErrorMessage(e, "Could not save the finding. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!control.auditorFinding) return;
    setDeleting(true);
    try {
      await api.del(`/auditor/clients/${orgId}/findings/${control.auditorFinding.id}`);
      onFindingDeleted(control.id);
    } catch {
      setSaveError("Failed to delete finding.");
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  async function openDoc(docId: string, fileName: string) {
    setViewLoading(docId);
    setViewError((p) => ({ ...p, [docId]: "" }));
    try {
      const { url } = await api.get<{ url: string }>(`/auditor/clients/${orgId}/documents/${docId}/view-url`);
      setViewDoc({ id: docId, fileName, url });
    } catch {
      setViewError((p) => ({ ...p, [docId]: "Failed to load — please try again." }));
    } finally {
      setViewLoading(null);
    }
  }

  const statusBtnBase: React.CSSProperties = {
    border: "1px solid",
    borderRadius: 3,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s",
  };

  const accent    = frameworkAccent(control.frameworkId);
  const ref       = displayRef(control.id, control.frameworkId);
  const soaStatus = normSoaStatus(control.soaEntry?.status) || "NOT_STARTED";

  return (
    <div style={{ ...card, padding: 0, overflow: "hidden", borderLeft: `3px solid ${accent}` }}>
      {/* Header row — the same shape the client and consultant see on the control pages */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => { if (!bulkMode) setExpanded((e) => !e); }}
        onKeyDown={(e) => { if (!bulkMode && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setExpanded((v) => !v); } }}
        style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", cursor: bulkMode ? "default" : "pointer", textAlign: "left" }}
      >
        {bulkMode && !bulkEligible(control) && (
          <span title="This clause must be approved by the consultant before you can record findings." style={{ width: 16, flexShrink: 0 }} />
        )}
        {bulkMode && bulkEligible(control) && (
          <input
            type="checkbox"
            aria-label={`Select ${ref} for approval`}
            checked={bulkSelected?.has(control.id) ?? false}
            onChange={(e) => { e.stopPropagation(); onBulkToggle?.(control.id); }}
            onClick={(e) => e.stopPropagation()}
            style={{ width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}
          />
        )}
        <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 11, color: accent, minWidth: 44, fontWeight: 700, flexShrink: 0 }}>{ref}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, flex: 1, minWidth: 0 }}>{displayControlTitle(control.id, control.name, ref)}</span>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end", flexShrink: 0 }}>
          <DocCountChip count={control.documents.length} />
          {control.soaEntry?.approvalStatus === "DRAFT" && <PendingApprovalChip />}
          {/* The client's own status, in the client's chip */}
          <ControlStatusBadge status={soaStatus} />
          {/* Auditor-only context: consultant sign-off on mandatory clauses, and this bureau's finding */}
          {control.kind === "CLAUSE" && (
            <StatusTag tone={consultantChip.tone}>{consultantChip.label}</StatusTag>
          )}
          {findingStatus && (
            <StatusTag tone={findingTone(findingStatus)}>
              Finding: {statusLabel(findingStatus)}
            </StatusTag>
          )}
        </div>
        <span style={{ color: theme.color.faint, fontSize: 12, marginLeft: 4 }}>{expanded ? "▲" : "▼"}</span>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: "0 16px 16px", borderTop: `1px solid ${theme.color.border}`, display: "flex", flexDirection: "column", gap: 20 }}>
          {/* What the control asks for — the same opening the client reads */}
          <div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginTop: 12 }}>
              <p style={{ fontSize: 13, color: theme.color.subtle, margin: 0, lineHeight: 1.6, flex: 1 }}>{control.description}</p>
              {/* Every source behind the requirement, live or as at an audit snapshot. Cyber Essentials
                  requirements have no Control row and keep their own audit view. */}
              {!CE_FRAMEWORK_IDS.has(control.frameworkId) && (
                <button
                  onClick={() => setTrailOpen(true)}
                  style={{ ...btnSecondary, fontSize: 11, padding: "4px 10px", flexShrink: 0 }}
                >
                  Evidence trail
                </button>
              )}
            </div>
            {trailOpen && (
              <EvidenceTrailDrawer orgId={orgId} controlId={control.id} controlName={control.name} onClose={() => setTrailOpen(false)} />
            )}

            {/* Required evidence, ticked where a linked document's name matches */}
            {control.requiredEvidence.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 6 }}>Required evidence</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {control.requiredEvidence.map((ev) => {
                    const hasDoc = control.documents.some((d) =>
                      d.fileName.toLowerCase().includes(ev.toLowerCase().split(" ")[0])
                    );
                    return (
                      <div key={ev} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12 }}>
                        <span style={{ color: hasDoc ? theme.color.success : theme.color.danger, display: "inline-flex", alignItems: "center", flexShrink: 0, marginTop: 2 }} aria-label={hasDoc ? "Provided" : "Missing"}>
                          {hasDoc ? <Check size={12} aria-hidden="true" /> : <X size={12} aria-hidden="true" />}
                        </span>
                        <span style={{ color: theme.color.subtle }}>{ev}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Section 2 — AI Compliance Assessment */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
              AI Compliance Assessment
            </div>
            {aiControl ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
                {(
                  [
                    { key: "evidenceFound", label: "Evidence Found", color: theme.color.success },
                    { key: "gaps", label: "Gaps", color: theme.color.danger },
                    { key: "recommendations", label: "Recommendations", color: theme.color.accentText },
                  ] as const
                ).map(({ key, label, color }) => (
                  <div
                    key={key}
                    style={{
                      background: `${color}11`,
                      border: `1px solid ${color}33`,
                      borderRadius: 3,
                      padding: 12,
                    }}
                  >
                    <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
                      {label}
                    </div>
                    {(aiControl![key] ?? []).length === 0 ? (
                      <div style={{ fontSize: 11, color: theme.color.faint, fontStyle: "italic" }}>None</div>
                    ) : (
                      (aiControl![key] ?? []).map((item: string, i: number) => (
                        <div key={i} style={{ fontSize: 11, color: theme.color.text, marginBottom: 4, lineHeight: 1.5 }}>
                          • {item}
                        </div>
                      ))
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: theme.color.faint, fontStyle: "italic" }}>
                No compliance check has been run for this organisation.
              </div>
            )}
          </div>

          {/* Section 3 — Evidence Documents (read-only viewer) */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
              Evidence Documents
            </div>
            {control.documents.length === 0 ? (
              <div style={{ fontSize: 12, color: theme.color.faint, fontStyle: "italic" }}>
                No documents linked to this control.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {control.documents.map((doc) => (
                  <div
                    key={doc.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      background: theme.color.panelAlt,
                      border: `1px solid ${theme.color.border}`,
                      borderRadius: 3,
                      padding: "8px 12px",
                    }}
                  >
                    <span style={{ fontSize: 12, color: theme.color.text, fontWeight: 500 }}>{doc.fileName}</span>
                    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, color: theme.color.faint }}>{fmtBytes(doc.sizeBytes)}</span>
                      <span style={{ fontSize: 11, color: theme.color.faint }}>{fmtDate(doc.uploadedAt)}</span>
                      {viewError[doc.id] && (
                        <span style={{ fontSize: 11, color: theme.color.dangerText }}>{viewError[doc.id]}</span>
                      )}
                      <button
                        onClick={() => openDoc(doc.id, doc.fileName)}
                        disabled={viewLoading === doc.id}
                        style={{
                          background: "none",
                          border: `1px solid ${theme.color.borderStrong}`,
                          borderRadius: 3,
                          padding: "3px 10px",
                          fontSize: 11,
                          color: theme.color.accentText,
                          cursor: viewLoading === doc.id ? "default" : "pointer",
                          fontWeight: 500,
                          opacity: viewLoading === doc.id ? 0.5 : 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        {viewLoading === doc.id && <Spinner size={10} />}
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Doc viewer modal */}
          {viewDoc && (
            <DocViewerModal
              fileName={viewDoc.fileName}
              url={viewDoc.url}
              onClose={() => setViewDoc(null)}
            />
          )}

          {/* Linked risks */}
          {control.risks.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
                Linked Risks ({control.risks.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {control.risks.map((r) => {
                  const rColor = r.residualScore !== null
                    ? r.residualScore >= 15 ? theme.color.dangerText
                    : r.residualScore >= 8 ? theme.color.warning
                    : theme.color.success
                    : theme.color.subtle;
                  return (
                    <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: theme.color.panelAlt, borderRadius: 3 }}>
                      <span style={{ flex: 1, fontSize: 12, color: theme.color.text }}>{r.title}</span>
                      {r.residualScore !== null && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: rColor }}>Score {r.residualScore}</span>
                      )}
                      <span style={{ fontSize: 10, color: theme.color.muted, textTransform: "capitalize" }}>{r.status.replace(/_/g, " ")}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Section 3b — Client self-assessment (SOA status) */}
          {control.soaEntry && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                Client Self-Assessment
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <ControlStatusBadge status={soaStatus} />
                {control.soaEntry.applicable !== null && (
                  <span style={{ fontSize: 11, color: theme.color.muted }}>
                    {control.soaEntry.applicable ? "Applicable" : "Not Applicable"}
                  </span>
                )}
                {control.soaEntry.approvalStatus && control.soaEntry.approvalStatus !== "pending" && (
                  <span style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: control.soaEntry.approvalStatus === "approved" ? theme.color.success : theme.color.warning,
                    background: control.soaEntry.approvalStatus === "approved" ? theme.color.successBg : theme.color.warningBg,
                    padding: "2px 8px",
                    borderRadius: 4,
                  }}>
                    SOA: {control.soaEntry.approvalStatus.charAt(0).toUpperCase() + control.soaEntry.approvalStatus.slice(1)}
                  </span>
                )}
              </div>
              {control.soaEntry.justification && (
                <div style={{ fontSize: 12, color: theme.color.text, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "8px 12px" }}>
                  <span style={{ fontWeight: 600, color: theme.color.muted, marginRight: 6 }}>{CE_FRAMEWORK_IDS.has(control.frameworkId) ? "Notes:" : "Justification:"}</span>
                  {control.soaEntry.justification}
                </div>
              )}
              {control.soaEntry.aiNote && (
                <div style={{ fontSize: 12, color: theme.color.accentText, background: theme.color.accentBg, border: `1px solid ${theme.color.accentText}33`, borderRadius: 3, padding: "8px 12px" }}>
                  <span style={{ fontWeight: 600, marginRight: 6 }}>AI Note:</span>
                  {control.soaEntry.aiNote}
                </div>
              )}
              {(control.recordsDisagree?.length ?? 0) > 0 && (
                <div style={{ fontSize: 12, color: theme.color.dangerText, background: theme.color.dangerBg, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "8px 12px" }}>
                  <span style={{ fontWeight: 600, marginRight: 6 }}>Records disagree:</span>
                  {control.recordsDisagree!.join("; ")}.
                </div>
              )}
            </div>
          )}

          {/* Section 3c — Consultant clause review */}
          {control.clauseReview && (control.clauseReview.notes || control.clauseReview.status) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                Consultant Review
              </div>
              <div style={{ background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "10px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
                {control.clauseReview.status && (
                  <div style={{ fontSize: 12 }}>
                    <span style={{ fontWeight: 600, color: theme.color.muted, marginRight: 6 }}>Status:</span>
                    <span style={{
                      color: control.clauseReview.status === "approved" ? theme.color.success : control.clauseReview.status === "needs_work" ? theme.color.warning : theme.color.muted,
                      fontWeight: 600,
                    }}>
                      {control.clauseReview.status === "approved" ? "Approved" : control.clauseReview.status === "needs_work" ? "Needs Work" : "Pending"}
                    </span>
                    {control.clauseReview.updatedAt && (
                      <span style={{ fontSize: 11, color: theme.color.faint, marginLeft: 8 }}>
                        {fmtDate(control.clauseReview.updatedAt)}
                      </span>
                    )}
                  </div>
                )}
                {control.clauseReview.notes && (
                  <div style={{ fontSize: 12, color: theme.color.text }}>
                    <span style={{ fontWeight: 600, color: theme.color.muted, marginRight: 6 }}>Notes:</span>
                    <em>{control.clauseReview.notes}</em>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section 3d — Client & consultant control comments */}
          {control.comments.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                Internal Notes ({control.comments.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {control.comments.map((c) => (
                  <div key={c.id} style={{ background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "8px 12px" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: theme.color.accentText }}>
                        {c.user.displayName ?? "Unknown"}
                      </span>
                      <span style={{ fontSize: 10, color: theme.color.faint, textTransform: "capitalize" }}>
                        {c.user.role.replace(/_/g, " ")}
                      </span>
                      <span style={{ fontSize: 10, color: theme.color.faint, marginLeft: "auto" }}>
                        {fmtDate(c.createdAt)}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.5 }}>{c.body}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section 4a — Evidence requests */}
          <EvidenceRequestsInline orgId={orgId} readOnly={readOnly} controlId={control.id} frameworkId={control.frameworkId} findingId={control.auditorFinding && control.auditorFinding.status !== "resolved" ? control.auditorFinding.id : null} />

          {/* Section 4b — Auditor Finding */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
              Auditor Finding
            </div>
            {readOnly ? (
              <div style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.5 }}>
                {control.auditorFinding
                  ? <><strong>{statusLabel(control.auditorFinding.status)}</strong>{control.auditorFinding.text ? ` — ${control.auditorFinding.text}` : ""}</>
                  : <span style={{ color: theme.color.faint }}>No finding recorded. Observers read an engagement and do not record findings.</span>}
              </div>
            ) : !formUnlocked ? (
              <div
                style={{
                  background: theme.color.panelAlt,
                  border: `1px solid ${theme.color.border}`,
                  borderRadius: 3,
                  padding: "14px 16px",
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                }}
              >
                <span style={{ color: theme.color.faint, fontSize: 13, fontWeight: 600 }}>Locked</span>
                <span style={{ fontSize: 12, color: theme.color.faint }}>
                  This clause must be approved by the consultant before you can record findings.
                </span>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {/* The client's own progress on this finding */}
                {control.auditorFinding?.clientStatus === "resolved" && control.auditorFinding.status !== "resolved" && (
                  <div style={{ fontSize: 12, color: theme.color.accentText, background: theme.color.accentBg, border: `1px solid ${theme.color.accent}`, borderRadius: 3, padding: "8px 12px", lineHeight: 1.5 }}>
                    <strong>The client marked this resolved</strong>
                    {control.auditorFinding.clientStatusAt ? ` on ${fmtDate(control.auditorFinding.clientStatusAt)}` : ""}.
                    {" "}Verify the evidence, then set <strong>Approved</strong> — or send it back with <strong>Needs Work</strong> or <strong>Observation</strong>, which returns it to the client's actions.
                  </div>
                )}
                {control.auditorFinding?.correctiveAction && (
                  <FindingActionStatus action={control.auditorFinding.correctiveAction} closureProposed={!!control.auditorFinding.closureProposed} />
                )}
                {control.auditorFinding?.clientStatus === "acknowledged" && control.auditorFinding.status !== "resolved" && (
                  <div style={{ fontSize: 11, color: theme.color.subtle }}>
                    Client acknowledged this finding{control.auditorFinding.clientStatusAt ? ` on ${fmtDate(control.auditorFinding.clientStatusAt)}` : ""}.
                  </div>
                )}
                {/* Status radio buttons */}
                <div style={{ display: "flex", gap: 8 }}>
                  {(
                    [
                      { value: "resolved" as FindingStatus, label: "Approved", color: theme.color.success, bg: theme.color.successBg },
                      { value: "open" as FindingStatus, label: "Needs Work", color: theme.color.danger, bg: theme.color.dangerBg },
                      { value: "acknowledged" as FindingStatus, label: "Observation", color: theme.color.accentText, bg: theme.color.accentBg },
                    ] as const
                  ).map((opt) => {
                    const isActive = draftStatus === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setDraftStatus(opt.value)}
                        style={{
                          ...statusBtnBase,
                          display: "inline-flex", alignItems: "center", gap: 4,
                          background: isActive ? opt.bg : "transparent",
                          borderColor: isActive ? opt.color : theme.color.border,
                          color: isActive ? opt.color : theme.color.muted,
                        }}
                      >
                        {opt.value === "resolved" && <Check size={12} aria-hidden="true" />}
                        {opt.label}
                      </button>
                    );
                  })}
                </div>

                {/* Templates + textarea label row */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                  <FindingTemplatesPicker
                    currentText={draftText}
                    onApply={(text) => setDraftText(text)}
                  />
                </div>

                {/* Textarea */}
                <textarea
                  value={draftText}
                  onChange={(e) => setDraftText(e.target.value)}
                  placeholder="Add your audit observations, findings or instructions for the client here…"
                  rows={4}
                  style={{
                    width: "100%",
                    background: "var(--c-inputBg)",
                    border: `1px solid ${theme.color.borderStrong}`,
                    borderRadius: 3,
                    padding: "10px 12px",
                    color: theme.color.text,
                    fontSize: 13,
                    boxSizing: "border-box",
                    outline: "none",
                    resize: "vertical",
                    fontFamily: theme.font,
                    lineHeight: 1.5,
                  }}
                />

                {/* Save row */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <button
                    onClick={handleSave}
                    disabled={saving || needsText}
                    title={needsText ? "Describe what needs attention: only an approval can be saved without words." : undefined}
                    style={{
                      ...btnPrimary,
                      opacity: saving || needsText ? 0.6 : 1,
                      cursor: saving || needsText ? "not-allowed" : "pointer",
                    }}
                  >
                    {saving ? "Saving…" : control.auditorFinding ? "Update Finding" : "Save Finding"}
                  </button>
                  <span style={{ fontSize: 11, color: theme.color.faint }}>
                    Visible to client
                  </span>
                  {control.auditorFinding?.createdAt && (
                    <span style={{ fontSize: 11, color: theme.color.faint }}>
                      First recorded {fmtDate(control.auditorFinding.createdAt)}
                    </span>
                  )}
                  {saveError && (
                    <span style={{ fontSize: 12, color: theme.color.dangerText }}>{saveError}</span>
                  )}
                  {control.auditorFinding && !confirmDelete && (
                    <button
                      onClick={() => setConfirmDelete(true)}
                      style={{ marginLeft: "auto", background: "none", border: "none", fontSize: 11, color: theme.color.danger, cursor: "pointer" }}
                    >
                      Delete finding
                    </button>
                  )}
                  {confirmDelete && (
                    <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                      <span style={{ color: theme.color.danger }}>Delete this finding?</span>
                      <button onClick={handleDelete} disabled={deleting} style={{ background: "none", border: "none", color: theme.color.danger, fontWeight: 700, cursor: "pointer", fontSize: 11 }}>Yes</button>
                      <button onClick={() => setConfirmDelete(false)} style={{ background: "none", border: "none", color: theme.color.muted, cursor: "pointer", fontSize: 11 }}>No</button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Item 11 — Finding comment thread */}
          {control.auditorFinding && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
                Discussion
                {comments.length > 0 && (
                  <span style={{ ...countTag, marginLeft: 6, display: "inline-block" }}>
                    {comments.length}
                  </span>
                )}
              </div>
              {comments.length === 0 && commentsLoaded && (
                <div style={{ fontSize: 12, color: theme.color.faint, marginBottom: 8 }}>No comments yet.</div>
              )}
              {comments.map((c) => (
                <div key={c.id} style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 8 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                      background: c.authorRole === "auditor" ? theme.color.auditorBg : theme.color.panelAlt,
                      color: c.authorRole === "auditor" ? theme.color.auditor : theme.color.muted,
                    }}>
                      {c.authorRole === "auditor" ? "Auditor" : "Client"}
                    </span>
                    <span style={{ fontSize: 11, color: theme.color.muted }}>{c.author.displayName ?? c.author.email}</span>
                    <span style={{ fontSize: 10, color: theme.color.faint }}>{fmtDate(c.createdAt)}</span>
                  </div>
                  <div style={{ fontSize: 13, color: theme.color.text, paddingLeft: 4, whiteSpace: "pre-wrap" }}>{c.text}</div>
                </div>
              ))}
              <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                <input
                  type="text"
                  placeholder="Add a comment…"
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handlePostComment(); } }}
                  style={{ flex: 1, background: theme.color.bg, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "6px 10px", fontSize: 12, color: theme.color.text, fontFamily: theme.font, outline: "none" }}
                />
                <button
                  onClick={handlePostComment}
                  disabled={commentSaving || !commentDraft.trim()}
                  style={{ ...btnPrimary, fontSize: 11, padding: "6px 12px", opacity: commentSaving || !commentDraft.trim() ? 0.5 : 1 }}
                >
                  {commentSaving ? "…" : "Send"}
                </button>
              </div>
            </div>
          )}

          {/* Private notes — bottom of expanded body */}
          <PrivateNoteField orgId={orgId} controlId={control.id} />
        </div>
      )}
    </div>
  );
}

// ── Auditor evidence tree view ─────────────────────────────────────────────
//
// In-platform hierarchical view of evidence scoped to the bureau's accreditation.
// Renders: Standard/Law → Domain → Clause/Control → Evidence files (with viewer).
// Export (ZIP + PDF) is a secondary action at the top of the view.

interface TreeDoc {
  id: string;
  fileName: string;
  sizeBytes: number;
  uploadedAt: string;
}
interface TreeControl {
  controlId: string;
  controlName: string;
  kind: string;
  docs: TreeDoc[];
}
interface TreeDomain {
  domain: string | null;
  controls: TreeControl[];
}
interface TreeFramework {
  frameworkId: string;
  label: string;
  totalDocs: number;
  domains: TreeDomain[];
}
interface EvidenceTree {
  orgName: string;
  frameworks: TreeFramework[];
  legal: Array<{ regulation: string; docs: TreeDoc[] }>;
}

// ── Sub-components ─────────────────────────────────────────────────────────

function TreeDocRow({
  doc,
  orgId,
}: {
  doc: TreeDoc;
  orgId: string;
}) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [viewUrl, setViewUrl] = useState<string | null>(null);

  async function handleView() {
    if (viewUrl) { window.open(viewUrl, "_blank", "noopener,noreferrer"); return; }
    setLoading(true); setErr("");
    try {
      const { url } = await api.get<{ url: string }>(`/auditor/clients/${orgId}/documents/${doc.id}/view-url`);
      setViewUrl(url);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setErr("Failed to load — try again.");
    } finally {
      setLoading(false);
    }
  }

  const ext = doc.fileName.split(".").pop()?.toLowerCase() ?? "";
  const isImage = ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext);
  const isPdf = ext === "pdf";
  const Icon = isPdf ? FileText : isImage ? ImageIcon : Paperclip;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "7px 12px 7px 36px",
        borderBottom: "1px solid var(--c-border)",
        background: "var(--c-panel)",
      }}
    >
      <Icon size={14} style={{ flexShrink: 0, color: "var(--c-subtle)" }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: "var(--c-heading)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {doc.fileName}
        </div>
        <div style={{ fontSize: 11, color: "var(--c-subtle)", marginTop: 1 }}>
          {fmtBytes(doc.sizeBytes)} · {fmtDate(doc.uploadedAt)}
        </div>
      </div>
      {err && <span style={{ fontSize: 11, color: theme.color.danger }}>{err}</span>}
      <button
        onClick={handleView}
        disabled={loading}
        style={{
          flexShrink: 0,
          background: "none",
          border: `1px solid var(--c-border)`,
          borderRadius: 3,
          padding: "4px 12px",
          fontSize: 12,
          color: loading ? "var(--c-subtle)" : "var(--c-accentText)",
          cursor: loading ? "not-allowed" : "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {loading ? "Loading…" : "View"}
      </button>
    </div>
  );
}

function TreeControlRow({
  ctrl,
  orgId,
  defaultOpen,
}: {
  ctrl: TreeControl;
  orgId: string;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={{ borderBottom: "1px solid var(--c-border)" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 12px 8px 24px",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ fontSize: 11, color: "var(--c-subtle)", width: 16, flexShrink: 0 }}>{open ? "▼" : "▶"}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--c-accentText)", flexShrink: 0 }}>
          {ctrl.controlId}
        </span>
        <span style={{ fontSize: 12, color: "var(--c-heading)", flex: 1 }}>{ctrl.controlName}</span>
        {ctrl.docs.length > 0 ? (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              background: "var(--c-accentBg)",
              color: "var(--c-accentText)",
              borderRadius: 3,
              padding: "2px 8px",
              flexShrink: 0,
            }}
          >
            {ctrl.docs.length} file{ctrl.docs.length !== 1 ? "s" : ""}
          </span>
        ) : (
          <span style={{ fontSize: 10, color: "var(--c-subtle)", flexShrink: 0 }}>No evidence</span>
        )}
      </button>
      {open && (
        <div>
          {ctrl.docs.length === 0 ? (
            <div style={{ padding: "8px 12px 8px 52px", fontSize: 12, color: "var(--c-subtle)", fontStyle: "italic" }}>
              No evidence uploaded for this control yet.
            </div>
          ) : (
            ctrl.docs.map((doc) => <TreeDocRow key={doc.id} doc={doc} orgId={orgId} />)
          )}
        </div>
      )}
    </div>
  );
}

function TreeDomainSection({
  domain,
  controls,
  orgId,
}: {
  domain: string | null;
  controls: TreeControl[];
  orgId: string;
}) {
  const [open, setOpen] = useState(true);
  const totalDocs = controls.reduce((n, c) => n + c.docs.length, 0);

  return (
    <div style={{ marginBottom: 2 }}>
      {domain && (
        <button
          onClick={() => setOpen(!open)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            background: "var(--c-surface, rgba(0,0,0,0.02))",
            border: "none",
            borderTop: "1px solid var(--c-border)",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <span style={{ fontSize: 10, color: "var(--c-subtle)", width: 14 }}>{open ? "▼" : "▶"}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--c-heading)", flex: 1 }}>{domain}</span>
          <span style={{ fontSize: 11, color: "var(--c-subtle)" }}>
            {controls.length} control{controls.length !== 1 ? "s" : ""} · {totalDocs} file{totalDocs !== 1 ? "s" : ""}
          </span>
        </button>
      )}
      {open && controls.map((ctrl) => (
        <TreeControlRow
          key={ctrl.controlId}
          ctrl={ctrl}
          orgId={orgId}
          defaultOpen={false}
        />
      ))}
    </div>
  );
}

function TreeFrameworkSection({
  fw,
  orgId,
  defaultOpen,
}: {
  fw: TreeFramework;
  orgId: string;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      style={{
        ...card,
        padding: 0,
        overflow: "hidden",
        marginBottom: 8,
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 16px",
          background: "var(--c-panel)",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ fontSize: 13, color: "var(--c-subtle)", width: 16, flexShrink: 0 }}>{open ? "▼" : "▶"}</span>
        <span style={{ fontSize: 15, fontWeight: 700, color: "var(--c-heading)", flex: 1 }}>{fw.label}</span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            background: fw.totalDocs > 0 ? "var(--c-successBg)" : "var(--c-surface)",
            color: fw.totalDocs > 0 ? "var(--c-successText, #065F46)" : "var(--c-subtle)",
            borderRadius: 3,
            padding: "3px 10px",
            flexShrink: 0,
          }}
        >
          {fw.totalDocs} evidence file{fw.totalDocs !== 1 ? "s" : ""}
        </span>
      </button>
      {open && (
        <div>
          {fw.domains.map((d, i) => (
            <TreeDomainSection key={d.domain ?? `__none_${i}`} domain={d.domain} controls={d.controls} orgId={orgId} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Export action bar (secondary to the in-platform view) ─────────────────

function ExportActionBar({ orgId }: { orgId: string }) {
  const [state, setState] = useState<"idle" | "building" | "ready" | "error">("idle");
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [fileCount, setFileCount] = useState(0);
  const [err, setErr] = useState("");

  async function generate() {
    setState("building"); setErr(""); setDownloadUrl(null);
    try {
      const r = await api.post<{ downloadUrl: string; fileCount: number }>(
        `/auditor/clients/${orgId}/export/package`, {}
      );
      setDownloadUrl(r.downloadUrl);
      setFileCount(r.fileCount);
      setState("ready");
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Failed to generate.");
      setState("error");
    }
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        ...card,
        padding: "10px 14px",
        flexWrap: "wrap",
      }}
    >
      <FileText size={14} color="var(--c-auditor, #7C3AED)" style={{ flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: "var(--c-subtle)", flex: 1 }}>
        Export this evidence as a structured ZIP + PDF for offline audit records.
      </span>
      {state !== "ready" && (
        <button
          onClick={generate}
          disabled={state === "building"}
          style={{
            ...btnPrimary,
            fontSize: 12,
            padding: "6px 14px",
            opacity: state === "building" ? 0.6 : 1,
            cursor: state === "building" ? "not-allowed" : "pointer",
            flexShrink: 0,
          }}
        >
          {state === "building" ? "Building…" : "Export Package"}
        </button>
      )}
      {state === "error" && (
        <span style={{ fontSize: 12, color: theme.color.danger }}>{err}</span>
      )}
      {state === "ready" && downloadUrl && (
        <>
          <span style={{ fontSize: 12, color: "var(--c-successText, #065F46)", fontWeight: 600 }}>
            {fileCount} file{fileCount !== 1 ? "s" : ""} ready
          </span>
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...btnPrimary, fontSize: 12, padding: "6px 14px", textDecoration: "none", display: "inline-block", flexShrink: 0 }}
          >
            Download ZIP
          </a>
          <button
            onClick={() => { setState("idle"); setDownloadUrl(null); }}
            style={{ background: "none", border: "none", fontSize: 12, color: "var(--c-subtle)", cursor: "pointer", padding: "6px 0", flexShrink: 0 }}
          >
            Reset
          </button>
        </>
      )}
    </div>
  );
}

// ── Main tree view ─────────────────────────────────────────────────────────

function AuditEvidenceTreeView({ orgId }: { orgId: string }) {
  const [tree, setTree] = useState<EvidenceTree | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true); setError("");
    api
      .get<EvidenceTree>(`/auditor/clients/${orgId}/evidence-tree`)
      .then(setTree)
      .catch(() => setError("Failed to load evidence map."))
      .finally(() => setLoading(false));
  }, [orgId]);

  if (loading) return <LoadingBlock />;
  if (error || !tree) return (
    <div style={{ padding: "24px 0", color: theme.color.danger, fontSize: 13 }}>{error || "Not found."}</div>
  );

  const totalDocs = tree.frameworks.reduce((n, f) => n + f.totalDocs, 0);
  const noScope = tree.frameworks.length === 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Export action bar */}
      {!noScope && <ExportActionBar orgId={orgId} />}

      {noScope ? (
        <div
          style={{
            ...emptyFrame,
            padding: "24px 20px",
            fontSize: 13,
            color: "var(--c-subtle)",
          }}
        >
          No frameworks within your bureau's accreditation scope are enrolled by this organisation.
        </div>
      ) : (
        <>
          {/* Summary line */}
          <div style={{ fontSize: 12, color: "var(--c-subtle)" }}>
            Showing {tree.frameworks.length} standard{tree.frameworks.length !== 1 ? "s" : ""}
            {" · "}{totalDocs} evidence file{totalDocs !== 1 ? "s" : ""} total
          </div>

          {/* Frameworks */}
          {tree.frameworks.map((fw, i) => (
            <TreeFrameworkSection key={fw.frameworkId} fw={fw} orgId={orgId} defaultOpen={i === 0} />
          ))}
        </>
      )}

      <div style={{ fontSize: 11, color: "var(--c-subtle)", fontStyle: "italic", paddingBottom: 8 }}>
        Read-only view scoped to your bureau's accreditation. Evidence files open in a new tab.
        This view does not modify any client data.
      </div>
    </div>
  );
}

// ── Evidence view (per-client audit layout) ───────────────────────────────
// The element of /auditor/clients/:orgId: loads the client detail, renders the
// header, the pending-review banner and the schedule / sign-off / period
// comparison panels, then the routed tab (evidence-map | controls | hardening)
// through an <Outlet>. The tab pages read the shared state via
// useAuditOutlet().

interface AuditOutletContext {
  orgId: string;
  /** An observer reads an engagement and never changes it: no write control is offered. */
  readOnly: boolean;
  detail: AuditorClientDetail;
  controls: ControlWithContext[];
  onFindingUpdated: (controlId: string, finding: AuditorFinding) => void;
  onFindingDeleted: (controlId: string) => void;
}

function useAuditOutlet(): AuditOutletContext {
  return useOutletContext<AuditOutletContext>();
}

function EvidenceView({ onDetail, readOnly = false }: { onDetail?: (client: AuditorNavClient) => void; readOnly?: boolean }) {
  const { orgId = "" } = useParams<{ orgId: string }>();
  const [signOffs, setSignOffs] = useState(0);
  // A refusal that a retry cannot change (not engaged, not granted, no such organisation).
  const [errorFinal, setErrorFinal] = useState(false);
  const [detail, setDetail] = useState<AuditorClientDetail | null>(null);
  const [controls, setControls] = useState<ControlWithContext[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const loadDetail = useCallback(() => {
    return api
      .get<AuditorClientDetail>(`/auditor/clients/${orgId}`)
      .then(async (d) => {
        // Cyber Essentials requirements arrive with every other standard's controls, each under
        // the engaged CE standard (CE+ first where both are engaged).
        setDetail(d);
        setControls(d.controls);
        // Feeds the sidebar's engagement context block when the client list
        // has not arrived yet (or does not carry this organisation).
        onDetail?.({
          id: d.id,
          name: d.name,
          sector: d.sector,
          frameworkIds: d.scopedFrameworkIds ?? [],
          score: d.latestCheck?.overallScore ?? null,
          cisHardening: d.cisHardening,
        });
      });
  }, [orgId, onDetail]);

  const loadInitial = useCallback(() => {
    setLoading(true);
    setError("");
    setErrorFinal(false);
    loadDetail()
      .catch((e: unknown) => {
        const final = e instanceof ApiError && (e.status === 403 || e.status === 404);
        setErrorFinal(final);
        setError(final ? apiErrorMessage(e, "You cannot open this organisation.") : "Failed to load client detail.");
      })
      .finally(() => setLoading(false));
  }, [loadDetail]);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  // Poll every 30 s so the auditor sees live evidence/status changes without refreshing.
  useEffect(() => {
    const interval = setInterval(() => { loadDetail().catch(() => {}); }, 30_000);
    return () => clearInterval(interval);
  }, [loadDetail]);

  const handleFindingUpdated = useCallback((controlId: string, finding: AuditorFinding) => {
    setControls((prev) =>
      // The PATCH answers with the finding row alone; keep the client's action summary until the next poll.
      prev.map((c) => (c.id === controlId ? {
        ...c,
        auditorFinding: { ...finding, correctiveAction: finding.correctiveAction ?? (c.auditorFinding?.id === finding.id ? c.auditorFinding.correctiveAction : null) },
      } : c))
    );
  }, []);

  const handleFindingDeleted = useCallback((controlId: string) => {
    setControls((prev) =>
      prev.map((c) => (c.id === controlId ? { ...c, auditorFinding: null } : c))
    );
  }, []);

  if (loading) return <LoadingBlock />;
  if (error && errorFinal) {
    return (
      <div className="page-pad">
        <div style={{ ...emptyFrame, maxWidth: 640, margin: "40px auto", padding: 32, display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
          <span style={{ fontSize: 13, color: theme.color.text }}>{error}</span>
          <Link to="/auditor/clients" style={{ fontSize: 13, color: theme.color.accentText }}>Back to My Clients</Link>
        </div>
      </div>
    );
  }
  if (error || !detail) return <ErrorBlock message={error || "Not found."} onRetry={loadInitial} />;

  // Only mandatory clauses require consultant approval — count just those
  const pendingCount = controls.filter(
    (c) => c.kind === "CLAUSE" && c.clauseReview?.status !== "approved"
  ).length;

  const outletContext: AuditOutletContext = { orgId, readOnly, detail, controls, onFindingUpdated: handleFindingUpdated, onFindingDeleted: handleFindingDeleted };
  async function generateAuditReportPdf() {
    if (!detail) return;
    const d = detail;
    setPdfGenerating(true);
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const marginL = 20;
      const marginR = 20;
      const contentW = pageW - marginL - marginR;
      let y = 0;

      const addPage = () => { doc.addPage(); y = 20; };
      const checkY = (needed: number) => { if (y + needed > pageH - 15) addPage(); };

      // Cover page
      doc.setFillColor(30, 58, 138);
      doc.rect(0, 0, pageW, 60, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont("helvetica", "bold");
      doc.text("Audit Report", marginL, 30);
      doc.setFontSize(13);
      doc.setFont("helvetica", "normal");
      doc.text(d.name, marginL, 42);
      if (d.bureau?.name) doc.text(`Audited by: ${d.bureau.name}`, marginL, 50);

      doc.setTextColor(0, 0, 0);
      y = 70;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const frameworks = Array.from(new Set(controls.map((c) => c.frameworkId))).join(", ");
      doc.text(`Frameworks: ${frameworks}`, marginL, y); y += 7;
      doc.text(`Report date: ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`, marginL, y); y += 7;
      if (d.sector) { doc.text(`Sector: ${d.sector}`, marginL, y); y += 7; }
      const geography = geographyLabel(d.geography);
      if (geography) { doc.text(`Geography: ${geography}`, marginL, y); y += 7; }

      // Summary stats
      y += 6;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text("Audit Summary", marginL, y); y += 7;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);

      const totalFindings = controls.filter((c) => c.auditorFinding).length;
      const openFindings = controls.filter((c) => c.auditorFinding?.status === "open").length;
      const ackFindings = controls.filter((c) => c.auditorFinding?.status === "acknowledged").length;
      const resolvedFindings = controls.filter((c) => c.auditorFinding?.status === "resolved").length;
      const noFinding = controls.filter((c) => !c.auditorFinding).length;

      const summaryLines = [
        `Total controls: ${controls.length}`,
        `No finding (approved): ${noFinding}`,
        `Findings raised: ${totalFindings}`,
        `  Open: ${openFindings}`,
        `  Observation (acknowledged): ${ackFindings}`,
        `  Resolved: ${resolvedFindings}`,
      ];
      if (d.latestCheck?.overallScore != null) summaryLines.unshift(`Overall compliance score: ${d.latestCheck.overallScore}%`);

      for (const line of summaryLines) { doc.text(line, marginL, y); y += 6; }

      // Findings table
      y += 6;
      addPage();
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text("Findings by Control", marginL, y); y += 8;

      const colW = [28, 60, 28, contentW - 28 - 60 - 28];
      const headers = ["Control ID", "Control Name", "Status", "Finding / Notes"];
      doc.setFillColor(30, 58, 138);
      doc.rect(marginL, y - 5, contentW, 7, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      let cx = marginL;
      for (let i = 0; i < headers.length; i++) {
        doc.text(headers[i], cx + 1, y); cx += colW[i];
      }
      doc.setTextColor(0, 0, 0);
      y += 4;

      const withFindings = controls.filter((c) => c.auditorFinding);
      const withoutFindings = controls.filter((c) => !c.auditorFinding);
      const sorted = [...withFindings, ...withoutFindings];

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      let rowIndex = 0;
      for (const c of sorted) {
        const statusLabel = c.auditorFinding
          ? c.auditorFinding.status === "open" ? "Open"
          : c.auditorFinding.status === "acknowledged" ? "Observation"
          : "Resolved"
          : "No finding";
        const findingText = c.auditorFinding?.text ?? "—";
        const wrappedName = doc.splitTextToSize(c.name, colW[1] - 2);
        const wrappedFinding = doc.splitTextToSize(findingText, colW[3] - 2);
        const rowLines = Math.max(wrappedName.length, wrappedFinding.length);
        const rowH = rowLines * 4.5 + 2;

        checkY(rowH + 2);
        if (rowIndex % 2 === 0) {
          doc.setFillColor(245, 247, 255);
          doc.rect(marginL, y - 3.5, contentW, rowH, "F");
        }

        cx = marginL;
        doc.text(c.id, cx + 1, y); cx += colW[0];
        doc.text(wrappedName, cx + 1, y); cx += colW[1];

        const statusColor = c.auditorFinding?.status === "open"
          ? [220, 38, 38] as [number, number, number]
          : c.auditorFinding?.status === "acknowledged"
          ? [180, 83, 9] as [number, number, number]
          : [22, 163, 74] as [number, number, number];
        doc.setTextColor(...statusColor);
        doc.text(statusLabel, cx + 1, y); cx += colW[2];
        doc.setTextColor(0, 0, 0);
        doc.text(wrappedFinding, cx + 1, y);

        y += rowH;
        rowIndex++;
      }

      // Footer on each page
      const totalPages = (doc.internal as { getNumberOfPages?: () => number }).getNumberOfPages?.() ?? 1;
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(120, 120, 120);
        doc.text(
          `${d.name} — Confidential Audit Report — Page ${p} of ${totalPages}`,
          pageW / 2,
          pageH - 8,
          { align: "center" }
        );
      }

      const safeName = d.name.replace(/[^a-z0-9]/gi, "_").toLowerCase();
      doc.save(`audit_report_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } finally {
      setPdfGenerating(false);
    }
  }

  return (
    <div className="page-pad">
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.color.heading }}>
              {detail.name}
            </h2>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>
              {[detail.sector, geographyLabel(detail.geography)].filter(Boolean).join(" · ")}
            </div>
          </div>
          <button
            onClick={generateAuditReportPdf}
            disabled={pdfGenerating}
            style={{
              ...btnPrimary,
              fontSize: 11,
              padding: "6px 13px",
              flexShrink: 0,
              opacity: pdfGenerating ? 0.6 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Download size={13} />
            {pdfGenerating ? "Generating" : "Download Audit Report"}
          </button>
          {detail.bureau && (
            <div
              style={{
                background: theme.color.auditorBg,
                border: `1px solid ${theme.color.auditor}33`,
                borderRadius: 3,
                padding: "6px 14px",
                flexShrink: 0,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.auditor }}>
                {detail.bureau.name}
              </div>
              {detail.bureau.accreditation && (
                <div style={{ fontSize: 10, color: theme.color.auditor, opacity: 0.7 }}>
                  {detail.bureau.accreditation}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pending consultant review banner */}
        {pendingCount > 0 && (
          <div
            style={{
              background: theme.color.warningBg,
              border: `1px solid ${theme.color.warning}`,
              borderRadius: 3,
              padding: "12px 16px",
              display: "flex",
              gap: 10,
              alignItems: "center",
            }}
          >
            <AlertTriangle size={16} color="var(--c-warningText)" style={{ flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: theme.color.warningText }}>
              <strong>{pendingCount} mandatory clause{pendingCount !== 1 ? "s" : ""} pending consultant review</strong>
              {" — "}The consultant must mark these as Approved before you can record findings.
            </span>
          </div>
        )}

        {/* Audit schedule + sign-off + period comparison panels */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
          <AuditSchedulePanel orgId={orgId} readOnly={readOnly} />
          <AuditSignOffPanel orgId={orgId} controls={controls} readOnly={readOnly} onCompleted={() => setSignOffs((n) => n + 1)} />
          <PeriodComparisonPanel orgId={orgId} controls={controls} latestScore={detail?.latestCheck?.overallScore ?? null} refreshKey={signOffs} />
        </div>

        {/* The routed tab: /evidence-map, /controls or /hardening (sidebar "Audit" section) */}
        <Outlet context={outletContext} />
      </div>
    </div>
  );
}

// ── Audit tab: Evidence Map ───────────────────────────────────────────────

function EvidenceMapTab() {
  const { orgId } = useAuditOutlet();
  return <AuditEvidenceTreeView orgId={orgId} />;
}

// ── Audit tabs: snapshots and sampling (pages/auditor/) ──────────────────

function SnapshotsRoute() {
  const { orgId, detail, readOnly } = useAuditOutlet();
  return <AuditSnapshotsTab orgId={orgId} frameworkIds={detail.scopedFrameworkIds ?? []} readOnly={readOnly} />;
}

function SamplingRoute() {
  const { orgId, detail, controls, readOnly } = useAuditOutlet();
  return (
    <AuditSamplingTab
      orgId={orgId}
      readOnly={readOnly}
      frameworkIds={detail.scopedFrameworkIds ?? []}
      controls={controls.filter((c) => !CE_FRAMEWORK_IDS.has(c.frameworkId)).map((c) => ({ id: c.id, frameworkId: c.frameworkId, name: c.name }))}
    />
  );
}

// ── Audit tab: CIS Hardening ──────────────────────────────────────────────

interface HardeningBenchmarkSummary {
  id: string; name: string; version: string; category: string;
  total: number; done: number; inProgress: number; notStarted: number;
}

function HardeningTab() {
  const { orgId, detail } = useAuditOutlet();
  const [benchmarks, setBenchmarks] = useState<HardeningBenchmarkSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<{ benchmarks: HardeningBenchmarkSummary[] }>(`/auditor/clients/${orgId}/hardening`)
      .then((d) => { if (!cancelled) setBenchmarks(d.benchmarks); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId]);

  return (
    <div>
      {loading && <LoadingBlock />}
      {!loading && benchmarks.length === 0 && (
        <div style={{ ...emptyFrame, color: theme.color.muted, padding: 32, fontSize: 13 }}>
          {detail.cisHardening === false
            ? "This organisation's edition of the platform does not include CIS hardening benchmarks."
            : "No CIS hardening benchmarks are active for this organisation."}
        </div>
      )}
      {!loading && benchmarks.map((b) => {
        const pct = b.total > 0 ? Math.round((b.done / b.total) * 100) : 0;
        return (
          <div key={b.id} style={{ ...card, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{b.name}</div>
                <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 2 }}>{b.version} · {b.category}</div>
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: theme.color.accentText }}>{pct}%</div>
            </div>
            <RulerProgress value={b.done} max={b.total} label={`${b.name} compliance`} />
            <div style={{ display: "flex", gap: 16, marginTop: 6, fontSize: 11, color: theme.color.subtle, flexWrap: "wrap" }}>
              <span style={{ color: theme.color.successText }}>{b.done} compliant</span>
              <span>{b.inProgress} in progress</span>
              <span style={{ color: theme.color.faint }}>{b.notStarted} not started</span>
              <span style={{ marginLeft: "auto" }}>{b.total} total controls</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Audit tab: Technical Verification (Cyber Essentials Plus) ─────────────
// The assessor's technical audit (backend lib/cePlusTests.ts): the scope checks,
// then one test per control area, each with its result, what was sampled, notes
// and the day it was done. Shown while CE+ is in this auditor's scope; the API
// checks that again and refuses an observer's write.

type CePlusResult = "pass" | "fail" | "not_tested";

interface CePlusTestRow {
  key: string;
  area: "scope" | "A4" | "A5" | "A6" | "A7" | "A8";
  title: string;
  description: string;
  sampleHint: string;
  result: CePlusResult;
  sample: string | null;
  notes: string | null;
  testedAt: string | null;
  testedBy: { displayName: string | null; email: string } | null;
  remediationDueAt: string | null;
}

interface CePlusResponse {
  summary: { outcome: "passed" | "remediation_required" | "in_progress" | "not_started"; passed: number; failed: number; notTested: number; total: number; remediationDueAt: string | null };
  canRecord: boolean;
  tests: CePlusTestRow[];
}

const CE_PLUS_AREA_LABELS: Record<CePlusTestRow["area"], string> = {
  scope: "Before testing",
  A4: "A4 Firewalls",
  A5: "A5 Secure configuration",
  A6: "A6 Security update management",
  A7: "A7 User access control",
  A8: "A8 Malware protection",
};

const CE_PLUS_RESULT_LABELS: Record<CePlusResult, string> = { pass: "Pass", fail: "Fail", not_tested: "Not tested" };

const CE_PLUS_OUTCOME: Record<CePlusResponse["summary"]["outcome"], { label: string; tone: "success" | "danger" | "muted" }> = {
  passed: { label: "Every test passed", tone: "success" },
  remediation_required: { label: "Remediation required", tone: "danger" },
  in_progress: { label: "Testing in progress", tone: "muted" },
  not_started: { label: "Not started", tone: "muted" },
};

function cePlusResultTone(result: CePlusResult): StatusTone {
  if (result === "pass") return "met";
  if (result === "fail") return "gap";
  return "none";
}

function TechnicalVerificationTab() {
  const { orgId } = useAuditOutlet();
  const [data, setData] = useState<CePlusResponse | null>(null);
  const [error, setError] = useState("");
  // Out of scope is not a passing failure: no Try again for it.
  const [refused, setRefused] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(() => {
    setError("");
    return api.get<CePlusResponse>(`/auditor/clients/${orgId}/ce-plus-verification`)
      .then(setData)
      .catch((e) => {
        const outOfScope = e instanceof ApiError && e.status === 403;
        setRefused(outOfScope);
        setError(outOfScope
          ? "Cyber Essentials Plus is not in your audit scope for this organisation."
          : "Failed to load the technical verification.");
      });
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  if (error) return <ErrorBlock message={error} onRetry={refused ? undefined : load} />;
  if (!data) return <LoadingBlock />;

  const outcome = CE_PLUS_OUTCOME[data.summary.outcome];
  const toneColour = outcome.tone === "success" ? theme.color.successText : outcome.tone === "danger" ? theme.color.dangerText : theme.color.muted;
  const areas = [...new Set(data.tests.map((t) => t.area))];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ ...card, display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: toneColour }}>{outcome.label}</div>
          <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 4, lineHeight: 1.5 }}>
            Every test must pass for Cyber Essentials Plus to be awarded. A fail has to be remediated and re-tested within 30 days of the test.
            A fail on one sampled device fails the population it represents.
          </div>
          {data.summary.remediationDueAt && (
            <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 6, fontWeight: 600 }}>
              Earliest remediation deadline: {fmtDateOnly(data.summary.remediationDueAt)}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 16, fontSize: 12, color: theme.color.muted }}>
          <span style={{ color: theme.color.successText, fontWeight: 600 }}>{data.summary.passed} passed</span>
          <span style={{ color: theme.color.dangerText, fontWeight: 600 }}>{data.summary.failed} failed</span>
          <span>{data.summary.notTested} not tested</span>
        </div>
      </div>
      {!data.canRecord && (
        <div style={{ fontSize: 12, color: theme.color.subtle }}>Observers can read the results but not record them.</div>
      )}

      {areas.map((area) => (
        <div key={area}>
          <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: 0.4, margin: "6px 0 8px" }}>{CE_PLUS_AREA_LABELS[area]}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {data.tests.filter((t) => t.area === area).map((t) => {
              return (
                <div key={t.key} style={{ ...card, padding: "14px 18px" }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: theme.color.heading }}>{t.title}</div>
                      <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 3, lineHeight: 1.5 }}>{t.description}</div>
                    </div>
                    <StatusTag tone={cePlusResultTone(t.result)}>
                      {CE_PLUS_RESULT_LABELS[t.result]}
                    </StatusTag>
                    {data.canRecord && editing !== t.key && (
                      <button type="button" onClick={() => setEditing(t.key)} style={{ ...btnSecondary, fontSize: 12, padding: "4px 10px" }}>
                        {t.result === "not_tested" && !t.notes ? "Record result" : "Edit"}
                      </button>
                    )}
                  </div>
                  {editing === t.key ? (
                    <CePlusResultForm orgId={orgId} test={t} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
                  ) : (t.sample || t.notes || t.testedAt) && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${theme.color.border}`, display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: theme.color.text }}>
                      {t.sample && <div><span style={{ color: theme.color.muted }}>Sampled: </span><span style={{ whiteSpace: "pre-wrap" }}>{t.sample}</span></div>}
                      {t.notes && <div><span style={{ color: theme.color.muted }}>Notes: </span><span style={{ whiteSpace: "pre-wrap" }}>{t.notes}</span></div>}
                      <div style={{ color: theme.color.subtle }}>
                        {t.testedAt ? `Tested ${fmtDateOnly(t.testedAt)}` : "Not yet tested"}
                        {t.testedBy ? ` · recorded by ${t.testedBy.displayName || t.testedBy.email}` : ""}
                        {t.remediationDueAt ? ` · remediate by ${fmtDateOnly(t.remediationDueAt)}` : ""}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function CePlusResultForm({ orgId, test, onCancel, onSaved }: { orgId: string; test: CePlusTestRow; onCancel: () => void; onSaved: () => void }) {
  const [result, setResult] = useState<CePlusResult>(test.result);
  const [sample, setSample] = useState(test.sample ?? "");
  const [notes, setNotes] = useState(test.notes ?? "");
  const [testedAt, setTestedAt] = useState(toDateInput(test.testedAt));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const needsDate = result !== "not_tested" && !testedAt;

  async function save() {
    setSaving(true);
    setError("");
    try {
      await api.put(`/auditor/clients/${orgId}/ce-plus-verification/${test.key}`, {
        result, sample: sample.trim() || null, notes: notes.trim() || null, testedAt: dateInputToIso(testedAt),
      });
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "The result could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const label: React.CSSProperties = { fontSize: 11, color: theme.color.muted, display: "flex", flexDirection: "column", gap: 3 };
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${theme.color.border}`, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <label style={label}>
          Result
          <select value={result} onChange={(e) => setResult(e.target.value as CePlusResult)} style={{ ...inputStyle, width: 150 }}>
            {(Object.keys(CE_PLUS_RESULT_LABELS) as CePlusResult[]).map((r) => <option key={r} value={r}>{CE_PLUS_RESULT_LABELS[r]}</option>)}
          </select>
        </label>
        <label style={label}>
          Date tested
          <input type="date" value={testedAt} max={todayInput()} onChange={(e) => setTestedAt(e.target.value)} style={{ ...inputStyle, width: 170 }} />
        </label>
      </div>
      <label style={label}>
        What was sampled
        <textarea value={sample} onChange={(e) => setSample(e.target.value)} rows={2} maxLength={4000} placeholder={test.sampleHint} style={{ ...inputStyle, resize: "vertical" }} />
      </label>
      <label style={label}>
        Notes
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={4000} placeholder="What was found, the rationale for a fail, or an advisory note" style={{ ...inputStyle, resize: "vertical" }} />
      </label>
      {error && <div role="alert" style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button type="button" onClick={save} disabled={saving || needsDate} style={{ ...btnPrimary, fontSize: 12, padding: "6px 14px", opacity: saving || needsDate ? 0.6 : 1 }}>
          {saving ? "Saving…" : "Save result"}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} style={{ ...btnSecondary, fontSize: 12, padding: "6px 12px" }}>Cancel</button>
        {needsDate && <span style={{ fontSize: 11, color: theme.color.subtle }}>A pass or fail needs the date the test was carried out.</span>}
      </div>
    </div>
  );
}

// ── Audit tab: Submissions (competent authorities) ────────────────────────
// The regulatory submissions the client sent to this authority (sent ones only;
// drafts never reach it). The authority records only its acknowledgement and
// its own reference; the record itself is the client's and stays as sent.

interface AuthoritySubmission {
  id: string;
  displayId: number | null;
  kind: string;
  regime: string | null;
  status: "submitted" | "acknowledged";
  payload: Record<string, unknown> | null;
  awareAt: string | null;
  dueAt: string | null;
  submittedAt: string | null;
  reference: string | null;
  acknowledgedAt: string | null;
}

interface AuthoritySubmissionsResponse {
  authority: string;
  regime: string | null;
  submissions: AuthoritySubmission[];
}

const SUBMISSION_KIND_LABELS: Record<string, string> = {
  incident_notification: "Incident notification",
  registration: "Registration or designation notice",
  annual_return: "Annual return",
  assessment_return: "Assessment return",
  evidence_pack: "Evidence pack",
  information_notice: "Response to an information notice",
};

/** A payload key as a label: awareAt -> "Aware at", impact_summary -> "Impact summary". */
const payloadLabel = (key: string) => {
  const words = key.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

function SubmissionsTab() {
  const { orgId } = useAuditOutlet();
  const [data, setData] = useState<AuthoritySubmissionsResponse | null>(null);
  const [error, setError] = useState("");
  const [refused, setRefused] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [acking, setAcking] = useState<string | null>(null);
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);
  const [ackError, setAckError] = useState("");

  const load = useCallback(() => {
    setError("");
    return api.get<AuthoritySubmissionsResponse>(`/auditor/clients/${orgId}/submissions`)
      .then(setData)
      .catch((e) => {
        const notForYou = e instanceof ApiError && e.status === 403;
        setRefused(notForYou);
        setError(notForYou
          ? (e.message || "Only a competent authority supervising this organisation reads its submissions.")
          : "Failed to load submissions.");
      });
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  async function acknowledge(id: string) {
    setSaving(true);
    setAckError("");
    try {
      await api.post(`/auditor/clients/${orgId}/submissions/${id}/acknowledge`, { reference: reference.trim() });
      setAcking(null);
      setReference("");
      await load();
    } catch (e) {
      setAckError(e instanceof ApiError ? e.message : "The acknowledgement could not be recorded. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (error) return <ErrorBlock message={error} onRetry={refused ? undefined : load} />;
  if (!data) return <LoadingBlock />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 13, color: theme.color.muted, lineHeight: 1.5 }}>
        Submissions this organisation has sent to {data.authority}{data.regime ? ` under ${data.regime}` : ""}. Record your reference once
        you have processed one; the submission itself stays exactly as it was sent.
      </div>
      {data.submissions.length === 0 && (
        <div style={{ ...emptyFrame, color: theme.color.muted, padding: 32, fontSize: 13 }}>
          This organisation has not sent you any submissions.
        </div>
      )}
      {data.submissions.map((sub) => {
        const fields = sub.payload && typeof sub.payload === "object"
          ? Object.entries(sub.payload).filter(([, v]) => v !== null && v !== "" && (typeof v !== "object" || Array.isArray(v)))
          : [];
        return (
          <div key={sub.id} style={{ ...card, padding: "14px 18px" }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: theme.color.heading }}>
                  {sub.displayId != null ? `SUB-${String(sub.displayId).padStart(3, "0")} · ` : ""}{SUBMISSION_KIND_LABELS[sub.kind] ?? sub.kind}
                </div>
                <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 3 }}>
                  {sub.submittedAt ? `Sent ${fmtDate(sub.submittedAt)}` : "Sent"}
                  {sub.dueAt ? ` · due ${fmtDate(sub.dueAt)}` : ""}
                  {sub.regime ? ` · ${sub.regime}` : ""}
                </div>
                {sub.status === "acknowledged" && (
                  <div style={{ fontSize: 12, color: theme.color.successText, marginTop: 4 }}>
                    Acknowledged{sub.acknowledgedAt ? ` ${fmtDate(sub.acknowledgedAt)}` : ""}{sub.reference ? ` · your reference ${sub.reference}` : ""}
                  </div>
                )}
              </div>
              <button type="button" onClick={() => setOpen(open === sub.id ? null : sub.id)} style={{ ...btnSecondary, fontSize: 12, padding: "4px 10px" }}>
                {open === sub.id ? "Hide details" : "Details"}
              </button>
              {sub.status === "submitted" && acking !== sub.id && (
                <button type="button" onClick={() => { setAcking(sub.id); setReference(""); setAckError(""); }} style={{ ...btnPrimary, fontSize: 12, padding: "4px 12px" }}>
                  Acknowledge…
                </button>
              )}
            </div>
            {open === sub.id && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${theme.color.border}`, display: "flex", flexDirection: "column", gap: 4 }}>
                {sub.awareAt && <div style={{ fontSize: 12 }}><span style={{ color: theme.color.muted }}>Became aware: </span>{fmtDate(sub.awareAt)}</div>}
                {fields.length === 0 && <div style={{ fontSize: 12, color: theme.color.subtle }}>No further details were included.</div>}
                {fields.map(([k, v]) => (
                  <div key={k} style={{ fontSize: 12, color: theme.color.text }}>
                    <span style={{ color: theme.color.muted }}>{payloadLabel(k)}: </span>
                    <span style={{ whiteSpace: "pre-wrap" }}>{Array.isArray(v) ? v.join(", ") : String(v)}</span>
                  </div>
                ))}
              </div>
            )}
            {acking === sub.id && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${theme.color.border}`, display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                <label style={{ fontSize: 11, color: theme.color.muted, display: "flex", flexDirection: "column", gap: 3 }}>
                  Your reference
                  <input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} style={{ ...inputStyle, width: 240 }} />
                </label>
                <button type="button" disabled={saving || !reference.trim()} onClick={() => acknowledge(sub.id)}
                  style={{ ...btnPrimary, fontSize: 12, padding: "6px 14px", opacity: saving || !reference.trim() ? 0.6 : 1 }}>
                  {saving ? "Recording…" : "Record acknowledgement"}
                </button>
                <button type="button" onClick={() => setAcking(null)} disabled={saving} style={{ ...btnSecondary, fontSize: 12, padding: "6px 12px" }}>Cancel</button>
                {ackError && <div role="alert" style={{ fontSize: 12, color: theme.color.dangerText, width: "100%" }}>{ackError}</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Audit tab: Controls & Findings ────────────────────────────────────────
// Laid out like the client's framework pages: standard -> mandatory clauses /
// domain sections -> control cards (components/controlStatus.tsx chips).

function ControlsFindingsTab() {
  const { orgId, readOnly, detail, controls, onFindingUpdated: handleFindingUpdated, onFindingDeleted: handleFindingDeleted } = useAuditOutlet();
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [bulkMessage, setBulkMessage] = useState("");
  const [soaFilter, setSoaFilter] = useState<string>("all");
  // ?control=<id>: My Actions links one finding here — the list opens narrowed to its control.
  const [tabParams] = useSearchParams();
  const controlParam = tabParams.get("control");
  const [search, setSearch] = useState(() => controlParam ?? "");
  useDeepLinkScroll(controlParam, controls.length > 0);
  const [kindFilter, setKindFilter] = useState<"all" | "CLAUSE" | "CONTROL">("all");
  const [frameworkFilter, setFrameworkFilter] = useState<string>("all");
  const [sectionsExpanded, setSectionsExpanded] = useState<Set<string> | "all" | "none">("all");
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);

  const enrolledFrameworks = Array.from(new Set(controls.map((c) => c.frameworkId)));
  // Scope is re-fetched every 30 s; if the filtered framework has left it, show everything.
  const activeFrameworkFilter = frameworkFilter !== "all" && !enrolledFrameworks.includes(frameworkFilter) ? "all" : frameworkFilter;

  const q = search.trim().toLowerCase();
  const filteredControls = controls.filter((c) => {
    if (filter === "none" ? !!c.auditorFinding : filter !== "all" && c.auditorFinding?.status !== filter) return false;
    if (soaFilter !== "all" && (normSoaStatus(c.soaEntry?.status) || "NOT_STARTED") !== soaFilter) return false;
    if (kindFilter !== "all" && c.kind !== kindFilter) return false;
    if (activeFrameworkFilter !== "all" && c.frameworkId !== activeFrameworkFilter) return false;
    if (q && !c.id.toLowerCase().includes(q) && !c.name.toLowerCase().includes(q)) return false;
    return true;
  });

  // Section keys across every framework in scope, for expand / collapse all.
  const allSectionKeys = enrolledFrameworks.flatMap((fw) =>
    sectionsFor(controls.filter((c) => c.frameworkId === fw)).map((sec) => `${fw}::${sec.key}`)
  );
  function isSectionExpanded(key: string): boolean {
    if (sectionsExpanded === "all") return true;
    if (sectionsExpanded === "none") return false;
    return sectionsExpanded.has(key);
  }
  function toggleSection(key: string) {
    setSectionsExpanded((prev) => {
      const current = new Set(prev === "all" ? allSectionKeys : prev === "none" ? [] : prev);
      current.has(key) ? current.delete(key) : current.add(key);
      return current;
    });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <SoaSummaryBar controls={controls} />

        {/* Search + filters + tree controls */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <input
            style={{ ...inputStyle, maxWidth: 360 }}
            placeholder="Search by control ID or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {([
              { value: "all",         label: "All" },
              { value: "NOT_STARTED", label: "Not assessed" },
              { value: "IN_PROGRESS", label: "In progress" },
              { value: "GAP",         label: "Gap identified" },
              { value: "COMPLIANT",   label: "Compliant" },
            ] as const).map((opt) => (
              <FilterPill key={opt.value} active={soaFilter === opt.value} onClick={() => setSoaFilter(opt.value)}>{opt.label}</FilterPill>
            ))}
            <div style={{ width: 1, height: 24, background: theme.color.border, flexShrink: 0 }} />
            {([
              { value: "all",          label: "Any finding" },
              { value: "none",         label: "No finding" },
              { value: "open",         label: "Needs work" },
              { value: "acknowledged", label: "Observation" },
              { value: "resolved",     label: "Approved" },
            ] as const).map((opt) => (
              <FilterPill key={opt.value} active={filter === opt.value} onClick={() => setFilter(opt.value)}>{opt.label}</FilterPill>
            ))}
            {allSectionKeys.length > 0 && (
              <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
                <FilterPill small active={false} onClick={() => setSectionsExpanded("all")}>↕ Expand all</FilterPill>
                <FilterPill small active={false} onClick={() => setSectionsExpanded("none")}>↕ Collapse all</FilterPill>
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {/* Cyber Essentials has no mandatory clauses: the kind filter only where there are some. */}
            {controls.some((c) => c.kind === "CLAUSE") && (["all", "CLAUSE", "CONTROL"] as const).map((k) => (
              <FilterPill key={k} active={kindFilter === k} onClick={() => setKindFilter(k)}>
                {k === "all" ? "All controls" : k === "CLAUSE" ? "Mandatory clauses" : "Controls"}
              </FilterPill>
            ))}
            {enrolledFrameworks.length > 1 && (
              <>
                <div style={{ width: 1, height: 24, background: theme.color.border, flexShrink: 0 }} />
                {["all", ...enrolledFrameworks].map((fw) => (
                  <FilterPill key={fw} small active={activeFrameworkFilter === fw} onClick={() => setFrameworkFilter(fw)}>
                    {fw === "all" ? "All frameworks" : (FRAMEWORK_LABELS[fw] ?? fw)}
                  </FilterPill>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Results count + bulk mode toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 12, color: theme.color.muted, flex: 1 }}>
            Showing {filteredControls.length} of {controls.length} controls
          </div>
          {bulkMessage && <span role="status" style={{ fontSize: 12, color: theme.color.dangerText }}>{bulkMessage}</span>}
          {!readOnly && <button
            onClick={() => { setBulkMode((v) => !v); setBulkSelected(new Set()); setBulkMessage(""); }}
            style={{
              background: bulkMode ? theme.color.accentBg : "none",
              border: `1px solid ${bulkMode ? theme.color.accent : theme.color.border}`,
              borderRadius: 3, padding: "5px 12px", fontSize: 11, fontWeight: 600,
              color: bulkMode ? theme.color.accentText : theme.color.muted, cursor: "pointer",
            }}
          >
            {bulkMode ? "Exit bulk select" : "Bulk select"}
          </button>}
          {bulkMode && bulkSelected.size > 0 && (
            <button
              disabled={bulkSaving}
              onClick={async () => {
                setBulkSaving(true);
                setBulkMessage("");
                // Only rows that can take a finding are offered a checkbox (bulkEligible), so this is all of them.
                const toApprove = [...bulkSelected].filter((id) => {
                  const c = controls.find((x) => x.id === id);
                  return c && bulkEligible(c);
                });
                const results = await Promise.allSettled(
                  toApprove.map(async (controlId) => {
                    const c = controls.find((x) => x.id === controlId)!;
                    let result: AuditorFinding;
                    if (c.auditorFinding) {
                      result = await api.patch<AuditorFinding>(`/auditor/clients/${orgId}/findings/${c.auditorFinding.id}`, { status: "resolved" });
                    } else {
                      result = await api.post<AuditorFinding>(`/auditor/clients/${orgId}/findings`, { controlId, text: "", status: "resolved" });
                    }
                    handleFindingUpdated(controlId, result);
                  })
                );
                const failed = results.filter((r): r is PromiseRejectedResult => r.status === "rejected");
                if (failed.length) {
                  setBulkMessage(`${failed.length} of ${toApprove.length} could not be approved: ${apiErrorMessage(failed[0].reason, "please try again.")}`);
                }
                setBulkSelected(new Set());
                setBulkMode(false);
                setBulkSaving(false);
              }}
              style={{ ...btnPrimary, fontSize: 11, padding: "5px 12px", opacity: bulkSaving ? 0.6 : 1, display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              {bulkSaving ? "Approving…" : <><Check size={12} aria-hidden="true" />Approve {bulkSelected.size} selected</>}
            </button>
          )}
        </div>

        {/* Tree: standard → mandatory clauses / domain sections → control cards */}
        {filteredControls.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {enrolledFrameworks.map((fw) => {
              const fwControls = filteredControls.filter((c) => c.frameworkId === fw);
              if (!fwControls.length) return null;
              const accent = frameworkAccent(fw);
              return (
                <div key={fw} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {enrolledFrameworks.length > 1 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: accent, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        {FRAMEWORK_LABELS[fw] ?? fw}
                      </span>
                    </div>
                  )}
                  {sectionsFor(fwControls).map((sec) => {
                    const key = `${fw}::${sec.key}`;
                    // Searching opens every section, as it does for the client.
                    const open = q ? true : isSectionExpanded(key);
                    const compliant = sec.controls.filter((c) => normSoaStatus(c.soaEntry?.status) === "COMPLIANT").length;
                    const gap       = sec.controls.filter((c) => normSoaStatus(c.soaEntry?.status) === "GAP").length;
                    return (
                      <div key={key}>
                        <ControlSectionHeader
                          accent={accent}
                          expanded={open}
                          title={sec.title}
                          count={sec.controls.length}
                          compliant={compliant}
                          gap={gap}
                          onToggle={() => toggleSection(key)}
                          disabled={!!q}
                        />
                        {open && (
                          <ControlSectionBody accent={accent}>
                            {sec.controls.map((c) => (
                              <div key={c.id} data-deeplink={c.id}>
                              <ClauseCard
                                control={c}
                                orgId={orgId}
                                resultJson={detail.latestCheck?.resultJson ?? null}
                                onFindingUpdated={handleFindingUpdated}
                                onFindingDeleted={handleFindingDeleted}
                                readOnly={readOnly}
                                bulkMode={bulkMode}
                                bulkSelected={bulkSelected}
                                onBulkToggle={(id) => setBulkSelected((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; })}
                              />
                              </div>
                            ))}
                          </ControlSectionBody>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {filteredControls.length === 0 && (
          <div style={{ ...emptyFrame, color: theme.color.muted, padding: 32, fontSize: 13 }}>
            {controls.length === 0
              ? "This organisation is not enrolled in any standard your bureau is accredited to audit."
              : "No controls match this filter."}
          </div>
        )}
    </div>
  );
}

// ── Bureau Profile (standards selection) ─────────────────────────────────

// Long names for the Bureau Profile; the list itself is the shared certifiable set,
// so it always matches what PUT /auditor/frameworks accepts.
const FRAMEWORK_OPTION_LABELS: Record<CertifiableFrameworkId, string> = {
  iso27001:              "ISO/IEC 27001:2022 — Information Security Management",
  cyber_essentials:      "Cyber Essentials",
  cyber_essentials_plus: "Cyber Essentials Plus",
  iso42001:              "ISO/IEC 42001:2023 — AI Management Systems",
  iso9001:               "ISO 9001:2015 — Quality Management Systems",
  iso45001:              "ISO 45001:2018 — Occupational Health & Safety",
  iso14001:              "ISO 14001:2015 — Environmental Management Systems",
  soc2:                  "SOC 2 — Trust Services Criteria",
  pci_dss:               "PCI DSS v4.0",
  iasme_ca_sole_trader:  "IASME Cyber Assurance — Sole Trader",
  iasme_ca_micro:        "IASME Cyber Assurance — Micro",
  iasme_ca_small:        "IASME Cyber Assurance — Small",
  iasme_ca_medium_large: "IASME Cyber Assurance — Medium/Large",
  ncsc_caf:              "NCSC Cyber Assessment Framework v4.0",
};
const FRAMEWORK_OPTIONS = CERTIFIABLE_FRAMEWORKS.map((id) => ({ id, label: FRAMEWORK_OPTION_LABELS[id] }));

interface BureauMarketplaceProfile {
  description:    string;
  website:        string;
  contactEmail:   string;
  profileVisible: boolean;
}

interface FrameworkPriceRow {
  frameworkId:    string;
  certFeePence:   number;
  annualFeePence: number;
}

/** The inline "Saved" confirmation next to a save button. */
function SavedNote() {
  return (
    <span role="status" style={{ fontSize: 13, color: theme.color.success, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4 }}>
      <Check size={13} aria-hidden="true" />
      Saved
    </span>
  );
}

/** GET /auditor/bureau/profile. `isAdmin` arrives once the route opens to every auditor; absent means the caller's own flag decides. */
interface BureauProfileResponse {
  bureau: {
    description: string | null;
    website: string | null;
    contactEmail: string | null;
    profileVisible: boolean;
    frameworkPrices: FrameworkPriceRow[];
  };
  isAdmin?: boolean;
}

function poundsLabel(pence: number): string {
  const pennies = pence % 100 === 0 ? 0 : 2;
  return pence > 0 ? `£${(pence / 100).toLocaleString("en-GB", { minimumFractionDigits: pennies, maximumFractionDigits: pennies })}` : "—";
}

const sectionTitle: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 };
const sectionCard: React.CSSProperties = { ...card, padding: 20 };

function BureauProfileView({ user }: { user: CurrentUser }) {
  // ── Accredited frameworks state ──
  const [selected, setSelected]       = useState<Set<string>>(new Set());
  const [fwLoading, setFwLoading]     = useState(true);
  const [fwSaving, setFwSaving]       = useState(false);
  const [fwSaved, setFwSaved]         = useState(false);
  const [fwError, setFwError]         = useState<string | null>(null);

  // ── Marketplace profile state ──
  const [profile, setProfile]         = useState<BureauMarketplaceProfile>({ description: "", website: "", contactEmail: "", profileVisible: false });
  const [profSaving, setProfSaving]   = useState(false);
  const [profSaved, setProfSaved]     = useState(false);
  const [profError, setProfError]     = useState<string | null>(null);
  // The profile GET answered 403: this auditor may see the accreditation but
  // not the listing or the fees (the route is opening to every auditor; until
  // then the notice replaces the two sections rather than a raw error).
  const [profForbidden, setProfForbidden] = useState(false);
  // `isAdmin` from the response, when the API sends it.
  const [apiIsAdmin, setApiIsAdmin]   = useState<boolean | null>(null);

  // ── Per-framework pricing state ──
  const [prices, setPrices]           = useState<FrameworkPriceRow[]>([]);
  const [pricesSaving, setPricesSaving] = useState(false);
  const [pricesSaved, setPricesSaved]   = useState(false);
  const [pricesError, setPricesError]   = useState<string | null>(null);

  // Read-only for everyone who is not a bureau administrator: the caller's
  // own flag and, once the API reports it, the response's isAdmin must both
  // agree (the API enforces requireBureauAdmin on every write regardless).
  const readOnly = !user.isBureauAdmin || apiIsAdmin === false;

  useEffect(() => {
    // The accreditation is open to every auditor; the profile may not be yet.
    const frameworks = api.get<{ frameworks: string[] }>("/auditor/frameworks")
      .then((d) => setSelected(new Set(d.frameworks ?? [])))
      .catch(() => setFwError("Failed to load the bureau's accredited standards."));
    const details = api.get<BureauProfileResponse>("/auditor/bureau/profile")
      .then((profData) => {
        setProfile({
          description:    profData.bureau.description    ?? "",
          website:        profData.bureau.website        ?? "",
          contactEmail:   profData.bureau.contactEmail   ?? "",
          profileVisible: profData.bureau.profileVisible ?? false,
        });
        setPrices(profData.bureau.frameworkPrices ?? []);
        setApiIsAdmin(typeof profData.isAdmin === "boolean" ? profData.isAdmin : null);
        setProfForbidden(false);
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 403) setProfForbidden(true);
        else setProfError("Failed to load the bureau profile.");
      });
    Promise.all([frameworks, details]).finally(() => setFwLoading(false));
  }, []);

  const toggleFramework = (id: string) => {
    if (readOnly) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setFwSaved(false);
  };

  const saveFrameworks = async () => {
    setFwSaving(true); setFwSaved(false); setFwError(null);
    try {
      try {
        await api.put<{ frameworks: string[] }>("/auditor/frameworks", { frameworks: Array.from(selected) });
      } catch (e) {
        // Removing a standard engaged clients rely on: the API names them; ask before going on.
        if (!(e instanceof ApiError && e.status === 409 && e.code === "engaged_clients")) throw e;
        if (!window.confirm(e.message)) return;
        await api.put<{ frameworks: string[] }>("/auditor/frameworks", { frameworks: Array.from(selected), confirmDroppedEngagements: true });
      }
      setFwSaved(true);
    } catch (e) {
      setFwError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setFwSaving(false);
    }
  };

  const saveProfile = async () => {
    setProfSaving(true); setProfSaved(false); setProfError(null);
    try {
      await api.patch("/auditor/bureau/profile", {
        description:    profile.description    || null,
        website:        profile.website        || null,
        contactEmail:   profile.contactEmail   || null,
        profileVisible: profile.profileVisible,
      });
      setProfSaved(true);
    } catch (e) {
      setProfError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setProfSaving(false);
    }
  };

  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});
  const setPriceField = (frameworkId: string, field: "certFeePence" | "annualFeePence", penceValue: number) => {
    setPrices((prev) => {
      const idx = prev.findIndex((p) => p.frameworkId === frameworkId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], [field]: penceValue };
        return next;
      }
      return [...prev, { frameworkId, certFeePence: field === "certFeePence" ? penceValue : 0, annualFeePence: field === "annualFeePence" ? penceValue : 0 }];
    });
    setPricesSaved(false);
  };

  const getPriceField = (frameworkId: string, field: "certFeePence" | "annualFeePence"): number =>
    prices.find((p) => p.frameworkId === frameworkId)?.[field] ?? 0;

  const savePrices = async () => {
    setPricesSaving(true); setPricesSaved(false); setPricesError(null);
    try {
      if (Object.values(priceDrafts).some((v) => parseFloat(v || "0") < 0 || !Number.isFinite(parseFloat(v || "0")))) {
        setPricesError("A fee must be a number of pounds, 0 or more.");
        return;
      }
      const accreditedPrices = prices.filter((p) => selected.has(p.frameworkId));
      await api.put("/auditor/bureau/pricing", { prices: accreditedPrices });
      setPricesSaved(true);
    } catch (e) {
      setPricesError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setPricesSaving(false);
    }
  };

  const accreditedFrameworks = FRAMEWORK_OPTIONS.filter((fw) => selected.has(fw.id));

  // A fee is shown exactly as stored and billed, pence included (AUD-31): what is typed is kept as
  // typed while editing, and a negative figure is never sent.
  const priceText = (pence: number) => (pence % 100 === 0 ? String(pence / 100) : (pence / 100).toFixed(2));
  const poundInput = (frameworkId: string, field: "certFeePence" | "annualFeePence", placeholder: string) => {
    const key = `${frameworkId}:${field}`;
    const stored = prices.find((p) => p.frameworkId === frameworkId);
    const shown = priceDrafts[key] ?? (stored ? priceText(stored[field]) : "");
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ fontSize: 13, color: theme.color.subtle }}>£</span>
        <input
          type="number"
          min={0}
          step={0.01}
          placeholder={placeholder}
          value={shown}
          onChange={(e) => {
            const raw = e.target.value;
            setPriceDrafts((d) => ({ ...d, [key]: raw }));
            const pounds = parseFloat(raw || "0");
            if (Number.isFinite(pounds) && pounds >= 0) setPriceField(frameworkId, field, Math.round(pounds * 100));
          }}
          onBlur={() => setPriceDrafts((d) => { const next = { ...d }; delete next[key]; return next; })}
          aria-invalid={parseFloat(shown || "0") < 0}
          style={{ ...inputStyle, width: 100, textAlign: "right" }}
        />
      </div>
    );
  };

  const errorBox = (msg: string) => (
    <div style={{ marginTop: 12, background: theme.color.dangerBg, border: `1px solid ${theme.color.danger}`, borderRadius: 3, padding: "10px 14px", fontSize: 13, color: theme.color.dangerText }}>
      {msg}
    </div>
  );

  const readOnlyValue = (value: string, placeholder: string) => (
    <div style={{ fontSize: 13, color: value ? theme.color.text : theme.color.faint, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {value || placeholder}
    </div>
  );

  return (
    <div className="page-pad">
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

        {/* Header */}
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.color.heading }}>Bureau Profile</h2>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: theme.color.subtle }}>
            {readOnly
              ? "Your bureau's accreditations, marketplace listing and per-standard audit fees. Only bureau administrators can change them."
              : "Configure your bureau's accreditations, marketplace listing, and per-standard audit fees."}
          </p>
        </div>

        {/* Name, accreditation body, accreditation and country change only through
            a request the platform owner reviews (the API refuses non-administrators). */}
        {!readOnly && <BureauChangeRequestPanel />}

        {/* ── Section 1: Accredited Standards ── */}
        <div style={sectionCard}>
          <div style={sectionTitle}>Accredited Standards</div>
          <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14 }}>
            {readOnly
              ? "The standards your bureau is accredited to audit."
              : "Select the standards your bureau is accredited to audit. Only these will appear in your pricing table."}
          </div>
          {fwLoading ? (
            <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>
          ) : readOnly ? (
            accreditedFrameworks.length === 0 ? (
              <div style={{ fontSize: 13, color: theme.color.faint }}>No accredited standards recorded yet.</div>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
                {accreditedFrameworks.map((fw) => (
                  <li key={fw.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: theme.color.text, padding: "8px 12px", borderRadius: 3, border: `1px solid ${theme.color.border}` }}>
                    <Check size={13} aria-hidden="true" style={{ color: theme.color.success, flexShrink: 0 }} />
                    {fw.label}
                  </li>
                ))}
              </ul>
            )
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {FRAMEWORK_OPTIONS.map((fw) => (
                <label
                  key={fw.id}
                  style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", padding: "10px 12px", borderRadius: 3, background: selected.has(fw.id) ? `${theme.color.accent}18` : "transparent", border: `1px solid ${selected.has(fw.id) ? theme.color.accent + "55" : theme.color.border}`, transition: "all 0.15s" }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(fw.id)}
                    onChange={() => toggleFramework(fw.id)}
                    style={{ accentColor: theme.color.accent, width: 16, height: 16, flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 13, color: selected.has(fw.id) ? theme.color.text : theme.color.muted, fontWeight: selected.has(fw.id) ? 500 : 400 }}>
                    {fw.label}
                  </span>
                </label>
              ))}
            </div>
          )}
          {fwError && errorBox(fwError)}
          {!readOnly && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
              <button onClick={saveFrameworks} disabled={fwSaving || fwLoading} style={{ ...btnPrimary, opacity: fwSaving || fwLoading ? 0.6 : 1 }}>
                {fwSaving ? "Saving…" : "Save Accreditations"}
              </button>
              {fwSaved && <SavedNote />}
            </div>
          )}
        </div>

        {profForbidden ? (
          <div style={{ ...sectionCard, fontSize: 13, color: theme.color.subtle, lineHeight: 1.5 }}>
            The marketplace listing and audit fees are available to bureau administrators only.
          </div>
        ) : (
          <>
            {/* ── Section 2: Marketplace Listing ── */}
            <div style={sectionCard}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                <div>
                  <div style={sectionTitle}>Marketplace Listing</div>
                  <div style={{ fontSize: 12, color: theme.color.subtle }}>
                    Clients on Self-Service + Documents (L2) and above can browse your listing; engaging your bureau needs Full Service (L5).
                  </div>
                </div>
                {readOnly ? (
                  <span style={{ fontSize: 12, fontWeight: 600, flexShrink: 0, marginLeft: 16, color: profile.profileVisible ? theme.color.success : theme.color.muted }}>
                    {profile.profileVisible ? "Published" : "Unpublished"}
                  </span>
                ) : (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0, marginLeft: 16 }}>
                    <input
                      type="checkbox"
                      checked={profile.profileVisible}
                      onChange={(e) => { setProfile((p) => ({ ...p, profileVisible: e.target.checked })); setProfSaved(false); }}
                      style={{ accentColor: theme.color.accent, width: 16, height: 16 }}
                    />
                    <span style={{ fontSize: 12, fontWeight: 600, color: profile.profileVisible ? theme.color.success : theme.color.muted }}>
                      {profile.profileVisible ? "Published" : "Unpublished"}
                    </span>
                  </label>
                )}
              </div>

              {fwLoading ? (
                <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.text, marginBottom: 6 }}>Description</div>
                    {readOnly ? readOnlyValue(profile.description, "No description yet.") : (
                      <textarea
                        rows={4}
                        placeholder="Tell clients about your bureau — specialisms, experience, geographic coverage…"
                        value={profile.description}
                        onChange={(e) => { setProfile((p) => ({ ...p, description: e.target.value })); setProfSaved(false); }}
                        style={{ ...inputStyle, width: "100%", resize: "vertical", boxSizing: "border-box" }}
                      />
                    )}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.text, marginBottom: 6 }}>Website</div>
                      {readOnly ? readOnlyValue(profile.website, "Not set") : (
                        <input
                          type="url"
                          placeholder="https://example.com"
                          value={profile.website}
                          onChange={(e) => { setProfile((p) => ({ ...p, website: e.target.value })); setProfSaved(false); }}
                          style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
                        />
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.text, marginBottom: 6 }}>Contact Email</div>
                      {readOnly ? readOnlyValue(profile.contactEmail, "Not set") : (
                        <input
                          type="email"
                          placeholder="audits@example.com"
                          value={profile.contactEmail}
                          onChange={(e) => { setProfile((p) => ({ ...p, contactEmail: e.target.value })); setProfSaved(false); }}
                          style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {profError && errorBox(profError)}
              {!readOnly && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
                  <button onClick={saveProfile} disabled={profSaving || fwLoading} style={{ ...btnPrimary, opacity: profSaving || fwLoading ? 0.6 : 1 }}>
                    {profSaving ? "Saving…" : "Save Listing"}
                  </button>
                  {profSaved && <SavedNote />}
                </div>
              )}
            </div>

            {/* ── Section 3: Per-Standard Pricing ── */}
            <div style={sectionCard}>
              <div style={sectionTitle}>Audit Fees</div>
              <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 16 }}>
                {readOnly
                  ? "Certification and annual surveillance fees per standard, as shown on the marketplace listing and on client invoices."
                  : "Set your certification and annual surveillance fees per standard. Shown on your marketplace listing and included in client invoices."}
              </div>

              {fwLoading ? (
                <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>
              ) : accreditedFrameworks.length === 0 ? (
                <div style={{ fontSize: 13, color: theme.color.faint, padding: "16px 0" }}>
                  {readOnly ? "No accredited standards recorded yet." : "No accredited standards selected. Save your accreditations above first."}
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid var(--c-line)" }}>
                        <th style={{ textAlign: "left", padding: "8px 0", fontWeight: 600, color: theme.color.subtle, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>Standard</th>
                        <th style={{ textAlign: "right", padding: "8px 12px", fontWeight: 600, color: theme.color.subtle, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>Cert Fee</th>
                        <th style={{ textAlign: "right", padding: "8px 0", fontWeight: 600, color: theme.color.subtle, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>Annual Fee</th>
                        <th style={{ textAlign: "right", padding: "8px 0 8px 16px", fontWeight: 600, color: theme.color.subtle, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>Monthly cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accreditedFrameworks.map((fw) => {
                        const cert   = getPriceField(fw.id, "certFeePence");
                        const annual = getPriceField(fw.id, "annualFeePence");
                        const monthly = cert > 0 || annual > 0
                          ? `£${((Math.ceil(cert / 36) + Math.ceil(annual / 12)) / 100).toFixed(2)}`
                          : "—";
                        return (
                          <tr key={fw.id} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
                            <td style={{ padding: "10px 0", color: theme.color.text, fontWeight: 500 }}>{fw.label}</td>
                            <td style={{ padding: "10px 12px", textAlign: "right", color: theme.color.text }}>{readOnly ? poundsLabel(cert) : poundInput(fw.id, "certFeePence", "0")}</td>
                            <td style={{ padding: "10px 0", textAlign: "right", color: theme.color.text }}>{readOnly ? poundsLabel(annual) : poundInput(fw.id, "annualFeePence", "0")}</td>
                            <td style={{ padding: "10px 0 10px 16px", textAlign: "right", color: theme.color.accentText, fontWeight: 600, fontSize: 12 }}>{monthly}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {pricesError && errorBox(pricesError)}
              {!readOnly && accreditedFrameworks.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
                  <button onClick={savePrices} disabled={pricesSaving || fwLoading} style={{ ...btnPrimary, opacity: pricesSaving || fwLoading ? 0.6 : 1 }}>
                    {pricesSaving ? "Saving…" : "Save Prices"}
                  </button>
                  {pricesSaved && <SavedNote />}
                </div>
              )}
            </div>
          </>
        )}

      </div>
    </div>
  );
}

// ── Profile & Security view ───────────────────────────────────────────────

function ProfileView({ user, onNameChange }: { user: CurrentUser; onNameChange: (name: string) => void }) {
  const [displayName, setDisplayName] = useState(user.displayName);
  const [nameMsg, setNameMsg] = useState("");
  const [nameSaving, setNameSaving] = useState(false);

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdMsg, setPwdMsg] = useState("");
  const [pwdError, setPwdError] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);

  async function saveName(e: React.FormEvent) {
    e.preventDefault();
    setNameSaving(true); setNameMsg("");
    try {
      const res = await api.patch<{ displayName: string }>("/auditor/profile", { displayName });
      onNameChange(res.displayName);
      setNameMsg("Saved.");
    } catch {
      setNameMsg("Failed to save.");
    } finally { setNameSaving(false); }
  }

  async function savePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwdError(""); setPwdMsg("");
    if (newPwd !== confirmPwd) { setPwdError("Passwords do not match."); return; }
    if (newPwd.length < 8) { setPwdError("Password must be at least 8 characters."); return; }
    setPwdSaving(true);
    try {
      await api.patch("/auditor/profile", { currentPassword: currentPwd, newPassword: newPwd });
      setPwdMsg("Password changed.");
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (err: unknown) {
      setPwdError(apiErrorMessage(err, "Failed to change password."));
    } finally { setPwdSaving(false); }
  }

  const inp: React.CSSProperties = {
    width: "100%", padding: "9px 12px", borderRadius: 3, fontSize: 13,
    border: `1px solid ${theme.color.borderStrong}`, background: theme.color.bg,
    color: theme.color.text, outline: "none", boxSizing: "border-box",
    fontFamily: theme.font,
  };

  return (
    <div className="page-pad">
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading }}>Profile &amp; Security</h2>
          <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle }}>Manage your display name and, if you have a local account, your password.</p>
        </div>

        {/* Display name */}
        <div style={{ ...card }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, marginBottom: 14 }}>Display name</div>
          <form onSubmit={saveName} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, color: theme.color.muted, fontWeight: 600 }}>Name</label>
              <input style={inp} value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, color: theme.color.muted, fontWeight: 600 }}>Email</label>
              <input style={{ ...inp, opacity: 0.6, cursor: "not-allowed" }} value={user.email} readOnly />
            </div>
            {nameMsg && <div style={{ fontSize: 12, color: nameMsg === "Saved." ? theme.color.success : theme.color.dangerText }}>{nameMsg}</div>}
            <div>
              <button type="submit" disabled={nameSaving || displayName === user.displayName} style={{ ...btnPrimary, opacity: nameSaving || displayName === user.displayName ? 0.5 : 1 }}>
                {nameSaving ? "Saving…" : "Save name"}
              </button>
            </div>
          </form>
        </div>

        {/* Password change — local accounts only */}
        {user.hasLocalPassword ? (
          <div style={{ ...card }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, marginBottom: 14 }}>Change password</div>
            <form onSubmit={savePassword} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: theme.color.muted, fontWeight: 600 }}>Current password</label>
                <input style={inp} type="password" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} required autoComplete="current-password" />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: theme.color.muted, fontWeight: 600 }}>New password</label>
                <input style={inp} type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required minLength={8} autoComplete="new-password" />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: theme.color.muted, fontWeight: 600 }}>Confirm new password</label>
                <input style={inp} type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required autoComplete="new-password" />
              </div>
              {pwdError && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{pwdError}</div>}
              {pwdMsg && <div style={{ fontSize: 12, color: theme.color.success }}>{pwdMsg}</div>}
              <div>
                <button type="submit" disabled={pwdSaving || !currentPwd || !newPwd || !confirmPwd} style={{ ...btnPrimary, opacity: pwdSaving || !currentPwd || !newPwd || !confirmPwd ? 0.5 : 1 }}>
                  {pwdSaving ? "Saving…" : "Change password"}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div style={{ ...card, background: theme.color.accentBg, border: `1px solid ${theme.color.accent}` }}>
            <div style={{ fontSize: 12, color: theme.color.accentText, lineHeight: 1.6 }}>
              You are signed in via an SSO provider (Microsoft or Google). Password management is handled by your identity provider.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Private auditor notes per control (localStorage) ─────────────────────

// Notes and finding templates belong to the signed-in auditor of this bureau, never to the browser:
// another auditor (or another bureau's auditor) on the same browser profile must not read them
// (AUD-37). AuditorPortal sets the owner before any tab renders. Earlier keys without an owner are
// left unread: whose they were cannot be known.
let auditorStorageOwner = "anonymous";
function setAuditorStorageOwner(userId: string, bureauId: string | null | undefined) {
  auditorStorageOwner = `${userId}_${bureauId ?? "none"}`;
}

function privateNoteKey(orgId: string, controlId: string) {
  return `auditor_note_v2_${auditorStorageOwner}_${orgId}_${controlId}`;
}

function PrivateNoteField({ orgId, controlId }: { orgId: string; controlId: string }) {
  const key = privateNoteKey(orgId, controlId);
  const [text, setText] = useState(() => {
    try { return localStorage.getItem(key) ?? ""; } catch { return ""; }
  });
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = (val: string) => {
    setText(val);
    setSaved(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try { localStorage.setItem(key, val); } catch { /* quota */ }
      setSaved(true);
    }, 600);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: open ? 8 : 0 }}>
        <button
          onClick={() => setOpen((v) => !v)}
          style={{
            background: "none", border: "none", cursor: "pointer", padding: 0,
            fontSize: 11, fontWeight: 700, color: theme.color.faint,
            textTransform: "uppercase", letterSpacing: "0.07em",
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"><path d="M2 10V7.5L7.5 2l2.5 2.5L4.5 10H2z"/><path d="M6.5 3.5l2 2"/></svg>
          Private Notes {text.trim() ? "●" : ""}
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}><path d="M2 3.5l3 3 3-3"/></svg>
        </button>
        {open && saved && <span style={{ fontSize: 10, color: theme.color.faint }}>Saved</span>}
        <span style={{ fontSize: 10, color: theme.color.faint }}>— only you see this, on this browser</span>
      </div>
      {open && (
        <textarea
          autoFocus
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="Scratchpad — notes only visible to you, not shared with the client…"
          rows={3}
          style={{
            width: "100%", boxSizing: "border-box",
            background: `${theme.color.warning}0a`,
            border: `1px dashed ${theme.color.warning}66`,
            borderRadius: 3, padding: "8px 10px",
            color: theme.color.text, fontSize: 12,
            fontFamily: theme.font, resize: "vertical", outline: "none",
          }}
        />
      )}
    </div>
  );
}

// ── Evidence requests (inline, inside ClauseCard) ────────────────────────

interface EvidenceRequest {
  id: string;
  controlId: string;
  frameworkId: string | null;
  message: string;
  status: "pending" | "acknowledged" | "fulfilled" | "withdrawn";
  createdAt: string;
  updatedAt: string;
  /** Returned when the request is raised: what the client will be offered to answer with. */
  suggestedDocuments?: Array<{ documentId: string; fileName: string; version: number }>;
  findingId?: string | null;
  /** What the client sent, recorded on fulfilment: the exact versions, and any filed since. */
  responseNote?: string | null;
  fulfilledAt?: string | null;
  documents?: Array<{ documentId: string | null; fileName: string; version: number; source: string; attachedAt: string; newerVersion: number | null }>;
}

function EvidenceRequestsInline({ orgId, controlId, frameworkId, findingId, readOnly }: { orgId: string; controlId: string; frameworkId: string; findingId?: string | null; readOnly?: boolean }) {
  const [forFinding, setForFinding] = useState(true);
  const [openError, setOpenError] = useState("");
  async function openAttached(documentId: string) {
    setOpenError("");
    try {
      const { url } = await api.get<{ url: string }>(`/auditor/clients/${orgId}/documents/${documentId}/download-url`);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setOpenError("Could not open the document — please try again.");
    }
  }
  const [requests, setRequests] = useState<EvidenceRequest[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    api.get<{ requests: EvidenceRequest[] }>(`/auditor/clients/${orgId}/evidence-requests`)
      .then((d) => setRequests(d.requests.filter((r) => r.controlId === controlId)))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [orgId, controlId]);

  const activeRequests = requests.filter((r) => r.status !== "withdrawn");

  const handleCreate = async () => {
    if (!message.trim()) return;
    setSaving(true);
    setSaveError("");
    try {
      const result = await api.post<EvidenceRequest>(`/auditor/clients/${orgId}/evidence-requests`, {
        controlId, frameworkId, message: message.trim(), ...(findingId && forFinding ? { findingId } : {}),
      });
      setRequests((prev) => [result, ...prev]);
      setMessage("");
      setShowForm(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Failed to create request.");
    } finally {
      setSaving(false);
    }
  };

  const handleWithdraw = async (reqId: string) => {
    try {
      await api.patch(`/auditor/clients/${orgId}/evidence-requests/${reqId}`, { status: "withdrawn" });
      setRequests((prev) => prev.map((r) => r.id === reqId ? { ...r, status: "withdrawn" as const } : r));
    } catch { /* silent */ }
  };

  const statusColor = (s: string) =>
    s === "fulfilled" ? theme.color.success :
    s === "acknowledged" ? theme.color.accentText :
    s === "withdrawn" ? theme.color.faint :
    theme.color.warning;

  const statusLabel = (s: string) =>
    s === "fulfilled" ? "Fulfilled" :
    s === "acknowledged" ? "Acknowledged" :
    s === "withdrawn" ? "Withdrawn" :
    "Pending";

  if (!loaded) return null;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>
          Evidence Requests
          {activeRequests.length > 0 && (
            <span style={{ ...countTag, marginLeft: 6, display: "inline-block" }}>
              {activeRequests.length}
            </span>
          )}
        </div>
        {!readOnly && (
          <button
            onClick={() => setShowForm((v) => !v)}
            style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "2px 9px", fontSize: 11, color: theme.color.accentText, cursor: "pointer", fontWeight: 600 }}
          >
            {showForm ? "Cancel" : "+ Request"}
          </button>
        )}
      </div>

      {showForm && (
        <div style={{ marginBottom: 10, display: "flex", flexDirection: "column", gap: 6 }}>
          <textarea
            autoFocus
            placeholder="Describe the evidence you need from the client…"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            style={{
              background: theme.color.bg, border: `1px solid ${theme.color.borderStrong}`,
              borderRadius: 3, padding: "8px 10px", color: theme.color.text,
              fontSize: 12, fontFamily: theme.font, resize: "vertical", outline: "none", width: "100%", boxSizing: "border-box",
            }}
          />
          {findingId && (
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: theme.color.muted }}>
              <input type="checkbox" checked={forFinding} onChange={(e) => setForFinding(e.target.checked)} />
              For the open finding on this control
            </label>
          )}
          {saveError && <div style={{ fontSize: 11, color: theme.color.dangerText }}>{saveError}</div>}
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={handleCreate} disabled={saving || !message.trim()} style={{ ...btnPrimary, fontSize: 11, padding: "5px 12px", opacity: saving || !message.trim() ? 0.6 : 1 }}>
              {saving ? "Sending…" : "Send Request"}
            </button>
          </div>
        </div>
      )}

      {requests.length === 0 ? (
        <div style={{ fontSize: 12, color: theme.color.faint }}>No evidence requests for this control.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {requests.map((r) => (
            <div key={r.id} style={{ background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "8px 12px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <div style={{ flex: 1, fontSize: 12, color: theme.color.text }}>{r.message}</div>
                <span style={{ fontSize: 10, fontWeight: 700, color: statusColor(r.status), flexShrink: 0 }}>
                  {statusLabel(r.status)}
                </span>
              </div>
              {r.suggestedDocuments && r.status === "pending" && (
                <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 4 }}>
                  {r.suggestedDocuments.length === 0
                    ? "Nothing is filed under this control yet, so the client has no document to answer with in one click."
                    : `The client is offered ${r.suggestedDocuments.length} document${r.suggestedDocuments.length === 1 ? "" : "s"} already filed under this control: ${r.suggestedDocuments.slice(0, 3).map((d) => `${d.fileName} (v${d.version})`).join(", ")}${r.suggestedDocuments.length > 3 ? ", …" : ""}.`}
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
                <span style={{ fontSize: 10, color: theme.color.faint }}>{fmtDate(r.createdAt)}</span>
                {r.findingId && <span style={{ fontSize: 10, color: theme.color.faint }}>For the finding</span>}
                {r.status !== "withdrawn" && r.status !== "fulfilled" && (
                  <button
                    onClick={() => handleWithdraw(r.id)}
                    style={{ background: "none", border: "none", fontSize: 10, color: theme.color.faint, cursor: "pointer", padding: 0, textDecoration: "underline" }}
                  >
                    Withdraw
                  </button>
                )}
              </div>
              {r.status === "fulfilled" && (r.documents?.length || r.responseNote) && (
                <div style={{ borderTop: "1px solid var(--c-line)", marginTop: 6, paddingTop: 6, fontSize: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: theme.color.muted, marginBottom: 3 }}>
                    Client sent{r.fulfilledAt ? ` on ${fmtDate(r.fulfilledAt)}` : ""}
                  </div>
                  {r.documents?.map((d, i) => (
                    <div key={`${d.documentId ?? "gone"}-${i}`} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                      {d.documentId
                        ? <button onClick={() => openAttached(d.documentId!)} style={{ background: "none", border: "none", padding: 0, color: theme.color.accent, cursor: "pointer", fontSize: 12, textAlign: "left" }}>{d.fileName}</button>
                        : <span style={{ color: theme.color.muted }}>{d.fileName} (since deleted)</span>}
                      <span style={{ fontSize: 10, color: "var(--c-ref)", fontVariantNumeric: "tabular-nums" }}>v{d.version}</span>
                      {d.newerVersion != null && <span style={{ fontSize: 10, color: theme.color.warningText }}>v{d.newerVersion} filed since</span>}
                    </div>
                  ))}
                  {r.responseNote && <div style={{ whiteSpace: "pre-wrap", color: theme.color.muted, marginTop: 3 }}>{r.responseNote}</div>}
                  {openError && <div style={{ fontSize: 11, color: theme.color.dangerText }}>{openError}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Finding templates (localStorage) ─────────────────────────────────────

const templatesKey = () => `auditor_finding_templates_v2_${auditorStorageOwner}`;

interface FindingTemplate {
  id: string;
  name: string;
  text: string;
}

function loadTemplates(): FindingTemplate[] {
  try {
    const raw = localStorage.getItem(templatesKey());
    return raw ? (JSON.parse(raw) as FindingTemplate[]) : [];
  } catch {
    return [];
  }
}

function saveTemplates(templates: FindingTemplate[]): void {
  try { localStorage.setItem(templatesKey(), JSON.stringify(templates)); } catch { /* quota — ignore */ }
}

function FindingTemplatesPicker({
  onApply,
  currentText,
}: {
  onApply: (text: string) => void;
  currentText: string;
}) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState<FindingTemplate[]>(() => loadTemplates());
  const [saveName, setSaveName] = useState("");
  const [saveMode, setSaveMode] = useState(false);

  const handleApply = (tpl: FindingTemplate) => {
    onApply(tpl.text);
    setOpen(false);
  };

  const handleSave = () => {
    const name = saveName.trim();
    if (!name || !currentText.trim()) return;
    const next: FindingTemplate[] = [
      ...templates,
      { id: Date.now().toString(), name, text: currentText.trim() },
    ];
    setTemplates(next);
    saveTemplates(next);
    setSaveName("");
    setSaveMode(false);
  };

  const handleDelete = (id: string) => {
    const next = templates.filter((t) => t.id !== id);
    setTemplates(next);
    saveTemplates(next);
  };

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => { setOpen((v) => !v); setSaveMode(false); }}
        style={{
          background: "none",
          border: `1px solid ${theme.color.border}`,
          borderRadius: 3,
          padding: "4px 10px",
          fontSize: 11,
          fontWeight: 600,
          color: theme.color.muted,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 5,
        }}
        title="Finding templates"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"><rect x="1" y="1" width="10" height="10" rx="1.5"/><path d="M3 4h6M3 6h6M3 8h4"/></svg>
        Templates
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 200,
            ...floatingSheet,
            width: 320,
            padding: 8,
          }}
        >
          {templates.length === 0 && !saveMode && (
            <div style={{ fontSize: 12, color: theme.color.subtle, padding: "8px 10px" }}>
              No templates yet. Type a finding and save it as a template for reuse.
            </div>
          )}

          {templates.map((tpl) => (
            <div
              key={tpl.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 8px",
                borderRadius: 3,
                cursor: "pointer",
              }}
            >
              <button
                onClick={() => handleApply(tpl)}
                style={{
                  flex: 1,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  padding: 0,
                  fontSize: 12,
                  color: theme.color.text,
                  fontWeight: 500,
                }}
                title={tpl.text}
              >
                {tpl.name}
              </button>
              <button
                onClick={() => handleDelete(tpl.id)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: theme.color.faint,
                  fontSize: 14,
                  lineHeight: 1,
                  padding: "0 2px",
                  flexShrink: 0,
                }}
                title="Delete template"
              >
                ×
              </button>
            </div>
          ))}

          <div style={{ borderTop: `1px solid ${theme.color.border}`, marginTop: 6, paddingTop: 6 }}>
            {!saveMode ? (
              <button
                onClick={() => setSaveMode(true)}
                disabled={!currentText.trim()}
                style={{
                  width: "100%",
                  background: "none",
                  border: `1px dashed ${theme.color.border}`,
                  borderRadius: 3,
                  padding: "6px 10px",
                  fontSize: 11,
                  color: currentText.trim() ? theme.color.accentText : theme.color.faint,
                  cursor: currentText.trim() ? "pointer" : "not-allowed",
                  textAlign: "left",
                }}
              >
                + Save current text as template
              </button>
            ) : (
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  autoFocus
                  type="text"
                  placeholder="Template name…"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") setSaveMode(false); }}
                  style={{
                    flex: 1,
                    background: theme.color.bg,
                    border: `1px solid ${theme.color.borderStrong}`,
                    borderRadius: 3,
                    padding: "5px 8px",
                    fontSize: 12,
                    color: theme.color.text,
                    fontFamily: theme.font,
                    outline: "none",
                  }}
                />
                <button
                  onClick={handleSave}
                  disabled={!saveName.trim()}
                  style={{
                    ...btnPrimary,
                    fontSize: 11,
                    padding: "5px 10px",
                    opacity: saveName.trim() ? 1 : 0.5,
                  }}
                >
                  Save
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Audit schedule panel ──────────────────────────────────────────────────

interface AuditScheduleEntry {
  id: string;
  auditType: string;
  scheduledDate: string;
  notes: string | null;
  updatedAt: string;
}

function AuditSchedulePanel({ orgId, readOnly }: { orgId: string; readOnly?: boolean }) {
  const [schedule, setSchedule] = useState<AuditScheduleEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [auditType, setAuditType] = useState<"initial" | "surveillance" | "recertification">("surveillance");
  const [scheduledDate, setScheduledDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    api.get<{ schedule: AuditScheduleEntry | null }>(`/auditor/clients/${orgId}/schedule`)
      .then((d) => {
        setSchedule(d.schedule);
        if (d.schedule) {
          setAuditType(d.schedule.auditType as typeof auditType);
          setScheduledDate(toDateInput(d.schedule.scheduledDate));
          setNotes(d.schedule.notes ?? "");
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgId]);

  const isOverdue = schedule ? new Date(schedule.scheduledDate) < new Date() : false;
  const daysUntil = schedule
    ? Math.ceil((new Date(schedule.scheduledDate).getTime() - Date.now()) / 86_400_000)
    : null;

  const handleSave = async () => {
    if (!scheduledDate) { setSaveError("Please select a date."); return; }
    setSaving(true);
    setSaveError("");
    try {
      const result = await api.put<{ schedule: AuditScheduleEntry }>(`/auditor/clients/${orgId}/schedule`, {
        auditType,
        scheduledDate: dateInputToIso(scheduledDate),
        notes: notes.trim() || undefined,
      });
      setSchedule(result.schedule);
      setOpen(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ ...card, padding: "16px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Audit Schedule</div>
          {loading ? (
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>Loading…</div>
          ) : schedule ? (
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span>{AUDIT_TYPE_LABELS[schedule.auditType] ?? schedule.auditType}</span>
              <span>·</span>
              <span style={{ fontWeight: 600, color: isOverdue ? theme.color.dangerText : daysUntil !== null && daysUntil <= 30 ? theme.color.warningText : theme.color.text }}>
                {fmtDateOnly(schedule.scheduledDate)}
              </span>
              {isOverdue && <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 3, background: theme.color.dangerBg, color: theme.color.dangerText, fontWeight: 700 }}>OVERDUE</span>}
              {!isOverdue && daysUntil !== null && daysUntil <= 30 && (
                <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 3, background: theme.color.warningBg, color: theme.color.warningText, fontWeight: 700 }}>
                  {daysUntil}d
                </span>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>No audit date scheduled.</div>
          )}
        </div>
        {!readOnly && (
          <button
            onClick={() => setOpen((v) => !v)}
            style={{
              background: "none",
              border: `1px solid ${theme.color.border}`,
              borderRadius: 3,
              padding: "6px 14px",
              fontSize: 12,
              color: theme.color.muted,
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            {open ? "Close" : schedule ? "Edit Schedule" : "Set Schedule"}
          </button>
        )}
      </div>

      {open && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(["initial", "surveillance", "recertification"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setAuditType(t)}
                style={{
                  border: `1px solid ${auditType === t ? theme.color.accent : theme.color.border}`,
                  borderRadius: 3,
                  padding: "5px 12px",
                  fontSize: 12,
                  fontWeight: auditType === t ? 700 : 400,
                  background: auditType === t ? theme.color.accentBg : "transparent",
                  color: auditType === t ? theme.color.accentText : theme.color.muted,
                  cursor: "pointer",
                }}
              >
                {AUDIT_TYPE_LABELS[t]}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <label style={{ fontSize: 12, color: theme.color.muted, flexShrink: 0 }}>Scheduled date</label>
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              style={{
                background: theme.color.bg,
                border: `1px solid ${theme.color.borderStrong}`,
                borderRadius: 3,
                padding: "6px 10px",
                fontSize: 13,
                color: theme.color.text,
                fontFamily: theme.font,
                outline: "none",
              }}
            />
          </div>
          <textarea
            placeholder="Notes (optional)…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            style={{
              background: theme.color.bg,
              border: `1px solid ${theme.color.borderStrong}`,
              borderRadius: 3,
              padding: "8px 12px",
              color: theme.color.text,
              fontSize: 13,
              fontFamily: theme.font,
              resize: "vertical",
              outline: "none",
            }}
          />
          {saveError && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{saveError}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, fontSize: 12, opacity: saving ? 0.6 : 1 }}>
              {saving ? "Saving…" : "Save Schedule"}
            </button>
            <button onClick={() => setOpen(false)} style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "6px 14px", fontSize: 12, color: theme.color.muted, cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Audit Sign-off panel ──────────────────────────────────────────────────

interface AuditCompletion {
  id: string;
  overallScore: number | null;
  totalFindings: number;
  openFindings: number;
  resolvedFindings: number;
  notes: string | null;
  completedAt: string;
  completedBy: { displayName: string | null; email: string };
}

function AuditSignOffPanel({ orgId, controls, readOnly, onCompleted }: { orgId: string; controls: ControlWithContext[]; readOnly?: boolean; onCompleted?: () => void }) {
  const [completions, setCompletions] = useState<AuditCompletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    api.get<{ completions: AuditCompletion[] }>(`/auditor/clients/${orgId}/completions`)
      .then((d) => setCompletions(d.completions))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgId]);

  const openFindings = controls.filter((c) => c.auditorFinding?.status === "open").length;
  const latest = completions[0] ?? null;

  const handleSignOff = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const result = await api.post<AuditCompletion>(`/auditor/clients/${orgId}/completions`, { notes: notes.trim() || undefined });
      setCompletions((prev) => [result, ...prev]);
      setNotes("");
      setConfirmOpen(false);
      setOpen(false);
      onCompleted?.();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Sign-off failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ ...card, padding: "16px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 200px", minWidth: 200 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Formal audit sign-off</div>
          {loading ? (
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>Loading…</div>
          ) : latest ? (
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>
              Last completed {fmtDate(latest.completedAt)} by {latest.completedBy.displayName ?? latest.completedBy.email}
              {latest.overallScore != null && ` · Score: ${latest.overallScore}%`}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>No audit completions recorded yet.</div>
          )}
        </div>
        {openFindings > 0 && (
          <div style={{ fontSize: 11, padding: "3px 10px", borderRadius: 3, background: theme.color.warningBg, color: theme.color.warningText, fontWeight: 600 }}>
            {openFindings} open finding{openFindings !== 1 ? "s" : ""}
          </div>
        )}
        {!readOnly && (
          <button
            onClick={() => setOpen((v) => !v)}
            style={{ ...btnPrimary, fontSize: 12, padding: "6px 14px" }}
          >
            {open ? "Close" : "Complete Audit"}
          </button>
        )}
      </div>

      {open && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          {openFindings > 0 && (
            <div style={{ background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, borderRadius: 3, padding: "8px 12px", fontSize: 12, color: theme.color.warningText }}>
              <AlertTriangle size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              {openFindings} finding{openFindings !== 1 ? "s" : ""} still open. You can still sign off but this will be recorded in the completion.
            </div>
          )}
          <textarea
            placeholder="Sign-off remarks (optional)…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            style={{
              background: theme.color.bg,
              border: `1px solid ${theme.color.borderStrong}`,
              borderRadius: 3,
              padding: "8px 12px",
              color: theme.color.text,
              fontSize: 13,
              fontFamily: theme.font,
              resize: "vertical",
              outline: "none",
            }}
          />
          {saveError && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{saveError}</div>}
          {!confirmOpen ? (
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setConfirmOpen(true)} style={{ ...btnPrimary, fontSize: 12 }}>
                Record Sign-off
              </button>
              <button onClick={() => setOpen(false)} style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "6px 14px", fontSize: 12, color: theme.color.muted, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 12, color: theme.color.text, fontWeight: 500 }}>Confirm formal completion of this audit?</span>
              <button onClick={handleSignOff} disabled={saving} style={{ ...btnPrimary, fontSize: 12, opacity: saving ? 0.6 : 1 }}>
                {saving ? "Saving…" : "Yes, complete"}
              </button>
              <button onClick={() => setConfirmOpen(false)} style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "6px 14px", fontSize: 12, color: theme.color.muted, cursor: "pointer" }}>
                Go back
              </button>
            </div>
          )}

          {completions.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Previous completions</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {completions.slice(0, 5).map((c) => (
                  <div key={c.id} style={{ fontSize: 11, color: theme.color.subtle, display: "flex", gap: 10 }}>
                    <span style={{ color: c.overallScore != null ? scoreColor(c.overallScore) : theme.color.faint, fontWeight: 600 }}>{c.overallScore != null ? `${c.overallScore}%` : "—"}</span>
                    <span>{fmtDate(c.completedAt)}</span>
                    <span>{c.completedBy.displayName ?? c.completedBy.email}</span>
                    {c.openFindings > 0 && <span style={{ color: theme.color.warningText }}>{c.openFindings} open</span>}
                    {c.notes && <span style={{ color: theme.color.subtle, fontStyle: "italic" }}>{c.notes.slice(0, 60)}{c.notes.length > 60 ? "…" : ""}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Period Comparison panel ───────────────────────────────────────────────

function MetricDelta({
  label,
  previous,
  current,
  delta,
  deltaColor,
}: {
  label: string;
  previous: string;
  current: string;
  delta: string | null;
  deltaColor: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div style={{ fontSize: 10, color: theme.color.muted, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.text }}>{current}</div>
      <div style={{ fontSize: 11, color: theme.color.subtle }}>
        was {previous}
        {delta != null && (
          <span style={{ marginLeft: 4, fontWeight: 700, color: deltaColor }}>
            {/^[+-]?0%?$/.test(delta) ? "· no change" : `· change ${delta}`}
          </span>
        )}
      </div>
    </div>
  );
}

function PeriodComparisonPanel({
  orgId,
  controls,
  latestScore,
  refreshKey = 0,
}: {
  orgId: string;
  controls: ControlWithContext[];
  latestScore: number | null;
  /** Bumped when a sign-off is recorded, so the new completion becomes the baseline at once. */
  refreshKey?: number;
}) {
  const [completions, setCompletions] = useState<AuditCompletion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ completions: AuditCompletion[] }>(`/auditor/clients/${orgId}/completions`)
      .then((d) => setCompletions(d.completions))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgId, refreshKey]);

  const last = completions[0] ?? null;

  const currentOpen = controls.filter((c) => c.auditorFinding?.status === "open").length;
  const currentResolved = controls.filter((c) => c.auditorFinding?.status === "resolved").length;

  const newSinceSignOff = last
    ? controls.filter(
        (c) => c.auditorFinding && new Date(c.auditorFinding.createdAt) > new Date(last.completedAt)
      ).length
    : 0;

  const scoreDelta =
    last != null && latestScore != null && last.overallScore != null
      ? latestScore - last.overallScore
      : null;
  const openDelta = last != null ? currentOpen - last.openFindings : null;
  const resolvedDelta = last != null ? currentResolved - last.resolvedFindings : null;

  const fmtDelta = (v: number) => (v > 0 ? `+${v}` : `${v}`);
  const scoreDeltaColor = (v: number) =>
    v > 0 ? theme.color.success : v < 0 ? theme.color.danger : theme.color.muted;
  const openDeltaColor = (v: number) =>
    v < 0 ? theme.color.success : v > 0 ? theme.color.danger : theme.color.muted;

  return (
    <div style={{ ...card, padding: "16px 20px" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 2 }}>
        Period Comparison
      </div>
      {loading ? (
        <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 4 }}>Loading…</div>
      ) : !last ? (
        <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 4 }}>
          No previous sign-off to compare against.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: theme.color.muted, marginBottom: 12 }}>
            vs {fmtDate(last.completedAt)} · {last.completedBy.displayName ?? last.completedBy.email}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <MetricDelta
              label="Compliance score"
              previous={last.overallScore != null ? `${last.overallScore}%` : "—"}
              current={latestScore != null ? `${latestScore}%` : "—"}
              delta={scoreDelta != null ? `${fmtDelta(scoreDelta)}%` : null}
              deltaColor={scoreDelta != null ? scoreDeltaColor(scoreDelta) : theme.color.muted}
            />
            <MetricDelta
              label="Open findings"
              previous={`${last.openFindings}`}
              current={`${currentOpen}`}
              delta={openDelta != null ? fmtDelta(openDelta) : null}
              deltaColor={openDelta != null ? openDeltaColor(openDelta) : theme.color.muted}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={{ fontSize: 10, color: theme.color.muted, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>New since sign-off</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: newSinceSignOff > 0 ? theme.color.danger : theme.color.text }}>
                {newSinceSignOff}
              </div>
              <div style={{ fontSize: 11, color: theme.color.subtle }}>findings raised</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={{ fontSize: 10, color: theme.color.muted, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>Net resolved</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: resolvedDelta != null && resolvedDelta > 0 ? theme.color.success : theme.color.text }}>
                {resolvedDelta != null ? fmtDelta(resolvedDelta) : "—"}
              </div>
              <div style={{ fontSize: 11, color: theme.color.subtle }}>vs last sign-off</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Bureau Analytics ─────────────────────────────────────────────────────

interface AnalyticsData {
  totalClients: number;
  avgScore: number | null;
  scoreDistribution: { green: number; amber: number; red: number; noData: number };
  totalFindings: { open: number; acknowledged: number; resolved: number };
  commonGaps: { controlId: string; controlName: string; frameworkId: string | null; openCount: number }[];
  sectorBreakdown: { sector: string; count: number; avgScore: number | null }[];
  clientScores: { id: string; name: string; sector: string | null; score: number | null; lastCheckAt: string | null; openFindings: number; totalFindings: number }[];
}

function BureauAnalyticsView({ onSelectClient }: { onSelectClient: (id: string) => void }) {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<AnalyticsData>("/auditor/analytics")
      .then(setData)
      .catch(() => setError("Failed to load analytics."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingBlock label="Loading analytics…" />;
  if (error || !data) return <ErrorBlock message={error ?? "No data"} onRetry={() => { setLoading(true); setError(null); api.get<AnalyticsData>("/auditor/analytics").then(setData).catch(() => setError("Failed to load analytics.")).finally(() => setLoading(false)); }} />;

  const statTile = (label: string, value: string | number, sub?: string, color?: string) => (
    <div style={{ ...card, flex: "1 1 140px", padding: "18px 20px" }}>
      <div style={{ fontSize: 28, fontWeight: 800, color: color ?? theme.color.heading, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.text, marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>{sub}</div>}
    </div>
  );

  const totalFindings = data.totalFindings.open + data.totalFindings.acknowledged + data.totalFindings.resolved;
  const distTotal = data.scoreDistribution.green + data.scoreDistribution.amber + data.scoreDistribution.red + data.scoreDistribution.noData;

  return (
    <div className="page-pad">
      <div>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: theme.color.heading, margin: 0, marginBottom: 4 }}>Bureau Analytics</h2>
          <p style={{ fontSize: 13, color: theme.color.subtle, margin: 0 }}>Portfolio-level overview across all assigned clients.</p>
        </div>

        {/* Stat tiles */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
          {statTile("Total Clients", data.totalClients)}
          {statTile("Avg Compliance Score", data.avgScore !== null ? `${data.avgScore}%` : "—", "across all clients", data.avgScore !== null ? scoreColor(data.avgScore) : theme.color.faint)}
          {statTile("Open Findings", data.totalFindings.open, "needing attention", data.totalFindings.open > 0 ? theme.color.danger : theme.color.success)}
          {statTile("Resolved Findings", data.totalFindings.resolved, `of ${totalFindings} total`, theme.color.success)}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          {/* Score distribution */}
          <div style={{ ...card, padding: "20px 24px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 16 }}>Score Distribution</div>
            {distTotal === 0 ? (
              <div style={{ fontSize: 13, color: theme.color.subtle }}>No compliance checks run yet.</div>
            ) : (
              <>
                <div style={{ display: "flex", borderRadius: 3, overflow: "hidden", height: 20, marginBottom: 14 }}>
                  {data.scoreDistribution.green > 0 && <div style={{ flex: data.scoreDistribution.green, background: theme.color.success }} title={`${data.scoreDistribution.green} ready`} />}
                  {data.scoreDistribution.amber > 0 && <div style={{ flex: data.scoreDistribution.amber, background: theme.color.warning }} title={`${data.scoreDistribution.amber} in progress`} />}
                  {data.scoreDistribution.red > 0 && <div style={{ flex: data.scoreDistribution.red, background: theme.color.danger }} title={`${data.scoreDistribution.red} needs attention`} />}
                  {data.scoreDistribution.noData > 0 && <div style={{ flex: data.scoreDistribution.noData, background: theme.color.faint }} title={`${data.scoreDistribution.noData} no data`} />}
                </div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  {[
                    { label: "Ready (≥80%)", count: data.scoreDistribution.green, color: theme.color.success },
                    { label: "In Progress (50–79%)", count: data.scoreDistribution.amber, color: theme.color.warning },
                    { label: "Needs Attention (<50%)", count: data.scoreDistribution.red, color: theme.color.danger },
                    { label: "No data", count: data.scoreDistribution.noData, color: theme.color.faint },
                  ].map((d) => (
                    <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: cbSafe(d.color), flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: theme.color.subtle }}>{d.count} {d.label}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Sector breakdown */}
          <div style={{ ...card, padding: "20px 24px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 16 }}>By Sector</div>
            {data.sectorBreakdown.length === 0 ? (
              <div style={{ fontSize: 13, color: theme.color.subtle }}>No sector data.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {data.sectorBreakdown.slice(0, 6).map((s) => (
                  <div key={s.sector} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 120, fontSize: 12, color: theme.color.text, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.sector}</div>
                    <RulerProgress value={s.avgScore ?? 0} fill={scoreColor(s.avgScore)} label={`${s.sector} average score`} style={{ flex: 1 }} />
                    <div style={{ width: 42, textAlign: "right", fontSize: 12, color: theme.color.subtle }}>{s.avgScore !== null ? `${s.avgScore}%` : "—"}</div>
                    <div style={{ width: 22, textAlign: "right", fontSize: 11, color: theme.color.faint }}>{s.count}</div>
                  </div>
                ))}
                {data.sectorBreakdown.length > 6 && (() => {
                  const rest = data.sectorBreakdown.slice(6);
                  const clients = rest.reduce((n, r) => n + r.count, 0);
                  return (
                    <div style={{ fontSize: 11, color: theme.color.subtle }}>
                      and {rest.length} more sector{rest.length === 1 ? "" : "s"} ({clients} client{clients === 1 ? "" : "s"}): {rest.map((r) => r.sector).join(", ")}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {/* Client scores list */}
          <div style={{ ...card, padding: "20px 24px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 16 }}>Client Scores</div>
            {data.clientScores.length === 0 ? (
              <div style={{ fontSize: 13, color: theme.color.subtle }}>No clients yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {data.clientScores.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => onSelectClient(c.id)}
                    style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", padding: "6px 0", borderRadius: 3, textAlign: "left", width: "100%" }}
                  >
                    <div style={{ width: 130, fontSize: 12, color: theme.color.text, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                    <RulerProgress value={c.score ?? 0} fill={scoreColor(c.score)} label={`${c.name} score`} style={{ flex: 1 }} />
                    <div style={{ width: 38, textAlign: "right", fontSize: 12, fontWeight: 700, color: c.score !== null ? scoreColor(c.score) : theme.color.faint }}>{c.score !== null ? `${c.score}%` : "—"}</div>
                    {c.openFindings > 0 && (
                      <div style={{ fontSize: 10, padding: "1px 6px", borderRadius: 3, background: theme.color.dangerBg, color: theme.color.dangerText, fontWeight: 600, flexShrink: 0 }}>{c.openFindings}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Common gaps */}
          <div style={{ ...card, padding: "20px 24px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Most Common Gaps</div>
            <div style={{ fontSize: 11, color: theme.color.subtle, marginBottom: 14 }}>Controls with open findings, by the number of clients affected</div>
            {data.commonGaps.length === 0 ? (
              <div style={{ fontSize: 13, color: theme.color.success, display: "flex", alignItems: "center", gap: 6 }}>
                <Check size={13} aria-hidden="true" />
                No open findings across your portfolio.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {data.commonGaps.map((g) => (
                  <div key={g.controlId} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: theme.color.text, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayControlTitle(g.controlId, g.controlName)}</div>
                      <div style={{ fontSize: 10, color: theme.color.faint }}>{displayControlRef(g.controlId)}{g.frameworkId ? ` · ${FRAMEWORK_LABELS[g.frameworkId] ?? g.frameworkId}` : ""}</div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.dangerText, flexShrink: 0 }}>{g.openCount}</div>
                    <div style={{ fontSize: 11, color: theme.color.subtle, flexShrink: 0 }}>{g.openCount === 1 ? "client" : "clients"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Bureau Team & Access view ─────────────────────────────────────────────
// Three blocks (docs/NAVIGATION.md section 6): the bureau's auditors, with
// each member's sub-role and, for framework specialists, the standards they
// may audit (the API enforces both on every request - lib/auditScope.ts; a
// specialist with no grants can see nothing); consultancy staff who reach the
// bureau through the consultant workspace (read-only, only when the API lists
// any); and the invitations not yet accepted, with resend / revoke. "Invite
// auditor" leads to the Invitations page, which owns the invite form.

interface BureauMember {
  id: string;
  displayName: string;
  email: string;
  auditorRole: string | null;
  isBureauAdmin: boolean;
  isActive: boolean;
  frameworkAccess: string[]; // list of frameworkId strings
  // How the member signs in. A "password" account carries an authenticator
  // app that a bureau admin can reset; optional so older payloads still render.
  signInMethod?: "microsoft" | "google" | "saml" | "password" | "none";
  totpEnabled?: boolean;
}

const SIGN_IN_LABEL: Record<NonNullable<BureauMember["signInMethod"]>, string> = {
  microsoft: "Microsoft", google: "Google", saml: "SAML", password: "Password + app", none: "Not signed in yet",
};

/**
 * Consultancy staff acting as auditors of this bureau: `staff[]` on
 * GET /auditor/bureau/members. Optional - older payloads omit it - and every
 * field beyond the identity is rendered only when present.
 */
interface BureauStaffMember {
  id: string;
  displayName: string;
  email: string;
  consultantRole?: string | null;
  isActive?: boolean;
}

interface BureauMembersResponse {
  members: BureauMember[];
  bureauFrameworks: string[];
  staff?: BureauStaffMember[];
}

const INVITATIONS_PATH = "/auditor/bureau/invitations";

const teamHeading: React.CSSProperties = { fontSize: 13, margin: 0 };
const teamBlockHint: React.CSSProperties = { fontSize: 12, color: theme.color.subtle, marginTop: 2, lineHeight: 1.5 };
const teamEmpty: React.CSSProperties = { ...emptyFrame, color: theme.color.muted, fontSize: 13, padding: "28px 16px" };
const teamPill: React.CSSProperties = { marginLeft: 8, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: theme.color.panelAlt, color: theme.color.subtle };

/** The sentence for a failed PATCH /auditor/bureau/members/:userId. */
function memberPatchError(err: unknown, fallback: string): string {
  const e = err as { payload?: { error?: unknown }; status?: number } | null;
  const code = typeof e?.payload?.error === "string" ? e.payload.error : null;
  if (code === "cannot_change_self") return "You cannot change your own role or administrator status. Ask another administrator.";
  if (code === "last_admin") return "This is the bureau's only active administrator. Make another member an administrator before removing or deactivating this one.";
  if (e?.status === 403) return "Bureau administrator access required.";
  return code ?? fallback;
}

function BureauTeamView({ currentUserId }: { currentUserId: string }) {
  const [members, setMembers] = useState<BureauMember[]>([]);
  const [staff, setStaff] = useState<BureauStaffMember[]>([]);
  const [bureauFrameworks, setBureauFrameworks] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState<string | null>(null);
  const [mfaResetId, setMfaResetId] = useState<string | null>(null);
  const [mfaResetNote, setMfaResetNote] = useState("");

  const [invites, setInvites] = useState<InviteSummary[]>([]);
  const [invitesLoading, setInvitesLoading] = useState(true);
  const [invitesError, setInvitesError] = useState("");

  useEffect(() => {
    api.get<BureauMembersResponse>("/auditor/bureau/members")
      .then((d) => {
        setMembers(d.members);
        setBureauFrameworks(d.bureauFrameworks);
        setStaff(Array.isArray(d.staff) ? d.staff : []);
      })
      .catch(() => setError("Failed to load team members. Bureau admin access required."))
      .finally(() => setLoading(false));
  }, []);

  const loadInvites = useCallback(() => {
    setInvitesError("");
    return api.get<{ invites: InviteSummary[] }>("/auditor/bureau/invites")
      .then((d) => setInvites(d.invites))
      .catch(() => setInvitesError("Failed to load invitations."))
      .finally(() => setInvitesLoading(false));
  }, []);

  useEffect(() => { loadInvites(); }, [loadInvites]);

  // Accepted invitations are members already; the rest can be resent or revoked.
  const openInvites = useMemo(() => invites.filter((i) => i.status !== "redeemed"), [invites]);

  const changeRole = async (memberId: string, auditorRole: string) => {
    setSaving(memberId + ":role");
    setError("");
    try {
      const updated = await api.patch<BureauMember>(`/auditor/bureau/members/${memberId}`, { auditorRole });
      setMembers((prev) => prev.map((m) => m.id === memberId ? updated : m));
    } catch (err: unknown) {
      setError(memberPatchError(err, "Failed to change the member's role. Please try again."));
    } finally {
      setSaving(null);
    }
  };

  // Administration is assigned here, to registered members (never through an
  // invitation). The API refuses your own row and the bureau's last
  // administrator; both come back as sentences.
  const toggleAdmin = async (memberId: string, isBureauAdmin: boolean) => {
    setSaving(memberId + ":admin");
    setError("");
    try {
      const updated = await api.patch<BureauMember>(`/auditor/bureau/members/${memberId}`, { isBureauAdmin });
      setMembers((prev) => prev.map((m) => m.id === memberId ? updated : m));
    } catch (err: unknown) {
      setError(memberPatchError(err, "Failed to change the member's administrator status. Please try again."));
    } finally {
      setSaving(null);
    }
  };

  // Deactivating signs the member out everywhere and refuses every request they
  // make until reactivated. Never yourself or the last active administrator (the
  // API answers both as sentences). Their history and findings are kept.
  const [statusConfirmId, setStatusConfirmId] = useState<string | null>(null);
  const setActive = async (member: BureauMember, isActive: boolean) => {
    setSaving(member.id + ":active");
    setError("");
    try {
      const updated = await api.patch<BureauMember>(`/auditor/bureau/members/${member.id}`, { isActive });
      setMembers((prev) => prev.map((m) => m.id === member.id ? updated : m));
      setStatusConfirmId(null);
    } catch (err: unknown) {
      setError(memberPatchError(err, isActive ? "Failed to reactivate the member. Please try again." : "Failed to deactivate the member. Please try again."));
    } finally {
      setSaving(null);
    }
  };

  // Clears a password member's authenticator app (lost phone and recovery
  // codes). They are signed out everywhere and enrol a new app after their
  // next password + emailed code. Never for yourself; SSO accounts have no
  // authenticator here to reset.
  const resetMemberMfa = async (member: BureauMember) => {
    setSaving(member.id + ":mfa");
    setError("");
    try {
      await api.post(`/auditor/bureau/members/${member.id}/mfa-reset`);
      setMembers((prev) => prev.map((m) => m.id === member.id ? { ...m, totpEnabled: false } : m));
      setMfaResetId(null);
      setMfaResetNote(`${member.displayName}'s authenticator has been reset. They will set up a new app at their next sign-in.`);
    } catch (err: unknown) {
      // The route answers { error: code, message: sentence }; ApiError's message already prefers the sentence.
      setError(err instanceof ApiError && err.status !== 403 ? err.message : memberPatchError(err, "Failed to reset the member's authenticator. Please try again."));
    } finally {
      setSaving(null);
    }
  };

  const toggleFramework = async (memberId: string, fwId: string) => {
    const member = members.find((m) => m.id === memberId);
    if (!member) return;
    const current = member.frameworkAccess;
    const next = current.includes(fwId)
      ? current.filter((f) => f !== fwId)
      : [...current, fwId];
    setSaving(memberId + ":" + fwId);
    setError("");
    try {
      await api.put(`/auditor/bureau/members/${memberId}/framework-access`, { frameworks: next });
      setMembers((prev) => prev.map((m) => m.id === memberId ? { ...m, frameworkAccess: next } : m));
    } catch (err: unknown) {
      const e = err as { payload?: { error?: string } };
      setError(typeof e?.payload?.error === "string" ? e.payload.error : "Failed to update framework access. Please try again.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="page-pad">
      <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.color.heading }}>Team & Access</h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: theme.color.subtle, lineHeight: 1.5 }}>
              Set each auditor's role. Framework Specialists see and audit only the standards you grant them;
              every other role covers all of the bureau's accredited standards.
            </p>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: theme.color.subtle, lineHeight: 1.5 }}>{BUREAU_ADMIN_NOTE}</p>
          </div>
          <Link
            to={INVITATIONS_PATH}
            style={{ ...btnPrimary, display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none", whiteSpace: "nowrap" }}
          >
            <UserPlus size={14} aria-hidden="true" />
            Invite auditor
          </Link>
        </div>

        {/* ── Auditors ── */}
        <section aria-labelledby="team-auditors" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <div id="team-auditors">
              <DimensionHeading as="h3" count={!loading && !error ? members.length : undefined} style={teamHeading}>Auditors</DimensionHeading>
            </div>
            <div style={teamBlockHint}>Members of your bureau who sign in to the auditor portal directly.</div>
          </div>

          {loading && <div style={{ fontSize: 13, color: theme.color.muted }}>Loading…</div>}
          {error && <div role="alert" style={{ fontSize: 13, color: theme.color.dangerText }}>{error}</div>}
          {mfaResetNote && (
            <div role="status" style={{ fontSize: 12, color: theme.color.successText, background: theme.color.successBg, border: `1px solid ${theme.color.success}`, borderRadius: theme.radius.sm, padding: "8px 12px" }}>
              {mfaResetNote}
            </div>
          )}

          {!loading && !error && members.length === 0 && (
            <div style={teamEmpty}>
              No auditors have joined yet.{" "}
              <Link to={INVITATIONS_PATH} style={{ color: theme.color.accentText, fontWeight: 600, textDecoration: "none" }}>
                Invite auditors to your bureau.
              </Link>
            </div>
          )}

          {!loading && members.map((m) => {
            const isSelf = m.id === currentUserId;
            const isSpecialist = m.auditorRole === "framework_specialist";
            const roleSaving = saving === m.id + ":role";
            const adminSaving = saving === m.id + ":admin";
            const mfaSaving = saving === m.id + ":mfa";
            const activeSaving = saving === m.id + ":active";
            const canResetMfa = !isSelf && m.signInMethod === "password" && m.totpEnabled === true;
            const adminTitle = isSelf
              ? "You cannot change your own administrator status. Ask another administrator."
              : m.isBureauAdmin ? "Remove bureau administration from this member" : "Make this member a bureau administrator";
            return (
              <div key={m.id} style={{ ...card, padding: "16px 20px", opacity: m.isActive ? 1 : 0.6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading }}>
                      {m.displayName}
                      {m.isBureauAdmin && (
                        <span style={{ ...teamPill, background: theme.color.auditorBg, color: theme.color.auditor }}>Admin</span>
                      )}
                      {isSelf && <span style={teamPill}>You</span>}
                      {!m.isActive && <span style={{ ...teamPill, background: theme.color.dangerBg, color: theme.color.dangerText }}>Deactivated</span>}
                    </div>
                    <div style={{ fontSize: 12, color: theme.color.muted }}>{m.email}</div>
                    {m.signInMethod && (
                      <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>
                        {SIGN_IN_LABEL[m.signInMethod]}{m.signInMethod === "password" && !m.totpEnabled && " · app not set up"}
                      </div>
                    )}
                  </div>
                  {isSelf ? (
                    <div style={{ fontSize: 11, padding: "2px 8px", borderRadius: 3, background: theme.color.panelAlt, color: theme.color.subtle, fontWeight: 600 }} title="Ask another bureau admin to change your role">
                      {AUDITOR_ROLE_LABELS[m.auditorRole ?? ""] ?? m.auditorRole ?? "Auditor"}
                    </div>
                  ) : (
                    <select
                      value={m.auditorRole ?? "auditor"}
                      disabled={roleSaving}
                      onChange={(e) => changeRole(m.id, e.target.value)}
                      style={{ ...inputStyle, width: "auto", fontSize: 12, padding: "4px 8px", opacity: roleSaving ? 0.5 : 1 }}
                      aria-label={`Role for ${m.displayName}`}
                    >
                      {Object.entries(AUDITOR_ROLE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  )}
                  <span title={adminTitle} style={{ display: "inline-flex" }}>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={m.isBureauAdmin}
                      aria-label={`Administrator: ${m.displayName}`}
                      disabled={isSelf || adminSaving}
                      onClick={() => toggleAdmin(m.id, !m.isBureauAdmin)}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 3,
                        cursor: isSelf || adminSaving ? "not-allowed" : "pointer",
                        background: m.isBureauAdmin ? theme.color.auditorBg : theme.color.bg,
                        color: m.isBureauAdmin ? theme.color.auditor : theme.color.muted,
                        border: `1px solid ${m.isBureauAdmin ? theme.color.auditor : theme.color.border}`,
                        opacity: adminSaving ? 0.5 : isSelf ? 0.7 : 1,
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: 26, height: 14, borderRadius: 3, position: "relative", flexShrink: 0,
                          background: m.isBureauAdmin ? theme.color.auditor : theme.color.border,
                        }}
                      >
                        <span style={{ position: "absolute", top: 2, left: m.isBureauAdmin ? 14 : 2, width: 10, height: 10, borderRadius: 3, background: theme.color.panel, transition: "left 120ms" }} />
                      </span>
                      Administrator
                    </button>
                  </span>
                  {canResetMfa && (
                    <button
                      type="button"
                      onClick={() => { setMfaResetId(mfaResetId === m.id ? null : m.id); setMfaResetNote(""); }}
                      disabled={mfaSaving}
                      title="Clear their authenticator app so they can enrol a new one"
                      style={{ ...btnSecondary, fontSize: 12, padding: "4px 10px", opacity: mfaSaving ? 0.5 : 1 }}
                    >
                      Reset MFA
                    </button>
                  )}
                  {!isSelf && (m.isActive ? (
                    <button
                      type="button"
                      onClick={() => setStatusConfirmId(statusConfirmId === m.id ? null : m.id)}
                      disabled={activeSaving}
                      title="Sign this member out everywhere and stop them signing in"
                      style={{ ...btnSecondary, fontSize: 12, padding: "4px 10px", color: theme.color.dangerText, opacity: activeSaving ? 0.5 : 1 }}
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setActive(m, true)}
                      disabled={activeSaving}
                      title="Let this member sign in again"
                      style={{ ...btnSecondary, fontSize: 12, padding: "4px 10px", opacity: activeSaving ? 0.5 : 1 }}
                    >
                      {activeSaving ? "Reactivating…" : "Reactivate"}
                    </button>
                  ))}
                </div>

                {statusConfirmId === m.id && m.isActive && (
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 12px", marginBottom: 12, borderRadius: theme.radius.sm, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}` }}>
                    <span style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.5 }}>
                      Deactivate <strong>{m.displayName}</strong>? They will be signed out everywhere and cannot sign in until you reactivate them.
                      Their findings and history stay on record.
                    </span>
                    <button
                      type="button"
                      onClick={() => setActive(m, false)}
                      disabled={activeSaving}
                      style={{ ...btnPrimary, fontSize: 12, padding: "4px 12px", opacity: activeSaving ? 0.6 : 1 }}
                    >
                      {activeSaving ? "Deactivating…" : "Yes, deactivate"}
                    </button>
                    <button type="button" onClick={() => setStatusConfirmId(null)} disabled={activeSaving} style={{ ...btnSecondary, fontSize: 12, padding: "4px 10px" }}>Cancel</button>
                  </div>
                )}

                {mfaResetId === m.id && (
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 12px", marginBottom: 12, borderRadius: theme.radius.sm, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}` }}>
                    <span style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.5 }}>
                      Reset the authenticator app for <strong>{m.displayName}</strong>? Only do this once you have confirmed it is really them —
                      they will be signed out everywhere and set up a new app after their next password and emailed code.
                    </span>
                    <button
                      type="button"
                      onClick={() => resetMemberMfa(m)}
                      disabled={mfaSaving}
                      style={{ ...btnPrimary, fontSize: 12, padding: "4px 12px", opacity: mfaSaving ? 0.6 : 1 }}
                    >
                      {mfaSaving ? "Resetting…" : "Yes, reset"}
                    </button>
                    <button type="button" onClick={() => setMfaResetId(null)} disabled={mfaSaving} style={{ ...btnSecondary, fontSize: 12, padding: "4px 10px" }}>Cancel</button>
                  </div>
                )}

                {isSpecialist ? (
                  <div>
                    <div style={{ fontSize: 11, color: theme.color.muted, marginBottom: 8 }}>Framework access — click to toggle:</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {bureauFrameworks.map((fw) => {
                        const active = m.frameworkAccess.includes(fw);
                        const key = m.id + ":" + fw;
                        const isSaving = saving === key;
                        return (
                          <button
                            key={fw}
                            type="button"
                            aria-pressed={active}
                            onClick={() => toggleFramework(m.id, fw)}
                            disabled={isSaving}
                            style={{
                              fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 3, cursor: "pointer",
                              background: active ? theme.color.successBg : theme.color.bg,
                              color: active ? theme.color.successText : theme.color.muted,
                              border: `1px solid ${active ? theme.color.success : theme.color.border}`,
                              opacity: isSaving ? 0.5 : 1,
                            }}
                          >
                            {FRAMEWORK_LABELS[fw] ?? fw}
                          </button>
                        );
                      })}
                      {bureauFrameworks.length === 0 && (
                        <span style={{ fontSize: 12, color: theme.color.faint }}>No frameworks accredited yet — set them in Bureau Profile.</span>
                      )}
                    </div>
                    {bureauFrameworks.length > 0 && m.frameworkAccess.filter((fw) => bureauFrameworks.includes(fw)).length === 0 && (
                      <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 8 }}>
                        No standards granted — this member cannot see any client work until you grant at least one.
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: theme.color.subtle }}>
                    Full access to all {bureauFrameworks.length} accredited framework{bureauFrameworks.length !== 1 ? "s" : ""} (not restricted).
                  </div>
                )}
              </div>
            );
          })}
        </section>

        {/* ── Consultancy staff acting as auditors (only when the API lists any) ── */}
        {!loading && staff.length > 0 && (
          <section aria-labelledby="team-staff" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <div id="team-staff">
                <DimensionHeading as="h3" count={staff.length} style={teamHeading}>Consultancy staff acting as auditors</DimensionHeading>
              </div>
              <div style={teamBlockHint}>
                They reach this bureau through the consultant workspace; their roles are managed in the consultancy's user settings.
              </div>
            </div>
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              {staff.map((s, i) => {
                const inactive = s.isActive === false;
                const roleLabel = s.consultantRole ? (CONSULTANT_ROLE_LABELS[s.consultantRole] ?? s.consultantRole) : null;
                return (
                  <div
                    key={s.id}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 20px", borderBottom: i < staff.length - 1 ? `1px solid ${theme.color.border}` : "none", opacity: inactive ? 0.6 : 1, flexWrap: "wrap" }}
                  >
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>
                        {s.displayName}
                        {s.id === currentUserId && <span style={teamPill}>You</span>}
                        {inactive && <span style={teamPill}>Inactive</span>}
                      </div>
                      <div style={{ fontSize: 12, color: theme.color.muted }}>{s.email}</div>
                    </div>
                    {roleLabel && (
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 3, background: theme.color.panelAlt, color: theme.color.subtle, fontWeight: 600 }}>
                        {roleLabel}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Pending invitations ── */}
        <section aria-labelledby="team-invites" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <div id="team-invites">
              <DimensionHeading as="h3" count={!invitesLoading && !invitesError ? openInvites.length : undefined} style={teamHeading}>Pending invitations</DimensionHeading>
            </div>
            <div style={teamBlockHint}>
              Invitations not yet accepted. Expired ones can be resent with a fresh link; the full history is on the{" "}
              <Link to={INVITATIONS_PATH} style={{ color: theme.color.accentText, textDecoration: "none" }}>Invitations</Link> page.
            </div>
          </div>

          {invitesLoading ? (
            <div style={{ fontSize: 13, color: theme.color.muted }}>Loading…</div>
          ) : invitesError ? (
            <div role="alert" style={{ fontSize: 13, color: theme.color.dangerText }}>
              {invitesError}{" "}
              <button type="button" onClick={() => { setInvitesLoading(true); loadInvites(); }} style={{ background: "none", border: "none", color: theme.color.accentText, cursor: "pointer", fontSize: 13, padding: 0 }}>Retry</button>
            </div>
          ) : openInvites.length === 0 ? (
            <div style={teamEmpty}>
              No invitations are waiting to be accepted.{" "}
              <Link to={INVITATIONS_PATH} style={{ color: theme.color.accentText, fontWeight: 600, textDecoration: "none" }}>
                Invite an auditor.
              </Link>
            </div>
          ) : (
            <InviteHistoryTable invites={openInvites} onResent={loadInvites} onResend={resendBureauInvite} onRevoke={revokeBureauInvite} />
          )}
        </section>

        {/* ── Sign-in policy (backend/src/lib/signInPolicy.ts) ── */}
        <section aria-label="Sign-in policy">
          <SignInPolicyPanel />
        </section>
      </div>
    </div>
  );
}

// ── AuditorActionsView ────────────────────────────────────────────────────

interface AuditorAction {
  id: string;
  type: string;
  title: string;
  description: string;
  href: string;
  severity: "high" | "medium" | "low";
  dueAt: string | null;
}

/**
 * Where an action item leads. Items carry a router path ("/auditor/clients/<orgId>");
 * anything else is ignored rather than navigated to.
 */
function actionItemPath(href: string): string | null {
  return href.startsWith("/auditor/") ? href : null;
}

function AuditorActionsView() {
  const navigate = useNavigate();
  const [items, setItems] = useState<AuditorAction[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ total: number; items: AuditorAction[] }>("/auditor/actions")
      .then((d) => { setTotal(d.total); setItems(d.items); })
      .catch((e: Error) => setError(e?.message ?? "Failed to load actions"))
      .finally(() => setLoading(false));
  }, []);

  const handleItemClick = (item: AuditorAction) => {
    const path = actionItemPath(item.href);
    if (path) navigate(path);
  };

  return (
    <div className="page-pad">
      <div>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: theme.color.heading, margin: 0, marginBottom: 4 }}>My Actions</h2>
          <p style={{ fontSize: 13, color: theme.color.subtle, margin: 0 }}>Findings your clients have marked resolved and need verifying, then open findings across your assigned clients.</p>
        </div>

        {loading && <LoadingBlock />}
        {error && <div style={{ ...card, color: theme.color.dangerText, fontSize: 13 }}>{error}</div>}

        {!loading && !error && total === 0 && (
          <div style={{ ...card, textAlign: "center", padding: "40px 24px" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 8, color: theme.color.successText }}><Check size={28} aria-hidden="true" /></div>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.successText, marginBottom: 4 }}>Nothing to do</div>
            <div style={{ fontSize: 13, color: theme.color.subtle }}>No findings awaiting verification and no open findings across your clients.</div>
          </div>
        )}

        {!loading && !error && total > 0 && (
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "12px 20px", borderBottom: `1px solid var(--c-border)`, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Findings</span>
              <span style={{ fontSize: 11, fontWeight: 700, background: "var(--c-warningBg)", color: "var(--c-warningText)", borderRadius: 3, padding: "1px 8px" }}>
                {total}
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {items.map((item, idx) => (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 20px",
                    background: "transparent", border: "none",
                    borderBottom: idx < items.length - 1 ? `1px solid var(--c-border)` : "none",
                    cursor: "pointer", textAlign: "left", width: "100%", transition: "background 0.12s",
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--c-panelAlt)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", borderRadius: 3, padding: "3px 7px", flexShrink: 0, marginTop: 1,
                    background: item.type === "verify_finding" ? "var(--c-accentBg)" : "var(--c-warningBg)",
                    color: item.type === "verify_finding" ? "var(--c-accentText)" : "var(--c-warningText)",
                  }}>
                    {item.type === "verify_finding" ? "Verify" : "Open"}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, marginBottom: 2 }}>{item.title}</div>
                    <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>{item.description}</div>
                  </div>
                  <ChevronRight size={14} color="var(--c-faint)" style={{ flexShrink: 0, marginTop: 3 }} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main AuditorPortal ────────────────────────────────────────────────────
// URL-routed under /auditor/* (docs/NAVIGATION.md section 1) on the shared
// AppShell. App.tsx mounts this for every auditor session whatever the URL
// says, so the catch-all normalises a stale path to /auditor/clients.

const AUDIT_TABS: readonly AuditTab[] = ["evidence-map", "controls", "registers", "snapshots", "sampling", "hardening", "technical-verification", "submissions"];

function auditTabOf(splat: string | undefined): AuditTab | null {
  const first = (splat ?? "").split("/")[0];
  return (AUDIT_TABS as readonly string[]).includes(first) ? first as AuditTab : null;
}

/** Bureau-admin pages send everyone else home (the API enforces requireBureauAdmin as well). */
function RequireBureauAdmin({ user, children }: { user: CurrentUser; children: React.ReactElement }) {
  if (!user.isBureauAdmin) return <Navigate to={AUDITOR_HOME} replace />;
  return children;
}

/**
 * GET /auditor/clients, loaded once per shell mount and re-fetched by the
 * pages that show it. Concurrent reloads collapse into the request in flight.
 */
function useAuditorClientsState(): AuditorClientsState {
  const [clients, setClients] = useState<AuditorClientSummary[]>([]);
  const [bureau, setBureau] = useState<BureauSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const inFlight = useRef(false);

  const reload = useCallback(() => {
    if (inFlight.current) return;
    inFlight.current = true;
    api.get<AuditorClientListResponse>("/auditor/clients")
      .then((d) => {
        setClients(d.clients);
        setBureau(d.bureau);
        setError("");
      })
      .catch(() => setError("Failed to load clients."))
      .finally(() => {
        inFlight.current = false;
        setLoading(false);
      });
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return useMemo(() => ({ clients, bureau, loading, error, reload }), [clients, bureau, loading, error, reload]);
}

export function AuditorPortal({ user: initialUser, onBackToConsultant }: { user: CurrentUser; onBackToConsultant?: () => void }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [user, setUser] = useState(initialUser);
  useEffect(() => { setUser(initialUser); }, [initialUser]);
  setAuditorStorageOwner(user.id, user.bureauId);

  const clientsState = useAuditorClientsState();
  const { clients, bureau } = clientsState;

  // Bureau name: /auth/me first, then the list response (older payloads).
  const bureauName = user.bureauName ?? bureau?.name ?? null;

  // The engagement in view, from the URL. The list row is preferred (it
  // carries the live framework scope); the detail the page loaded fills in
  // until the list arrives or when the organisation is not in it.
  const clientMatch = matchPath({ path: "/auditor/clients/:orgId/*" }, pathname);
  const selectedOrgId = clientMatch?.params.orgId ?? null;
  const activeTab = auditTabOf(clientMatch?.params["*"]);
  const [detailClient, setDetailClient] = useState<AuditorNavClient | null>(null);
  const selectedSummary = selectedOrgId ? clients.find((c) => c.id === selectedOrgId) ?? null : null;
  const selectedClient = useMemo<AuditorNavClient | null>(() => {
    if (!selectedOrgId) return null;
    if (selectedSummary) {
      return {
        id: selectedSummary.id,
        name: selectedSummary.name,
        sector: selectedSummary.sector,
        frameworkIds: selectedSummary.frameworkIds,
        score: selectedSummary.latestCheck?.overallScore ?? null,
      };
    }
    return detailClient && detailClient.id === selectedOrgId ? detailClient : null;
  }, [selectedOrgId, selectedSummary, detailClient]);

  const switchToConsultant = useMemo(() => {
    if (!onBackToConsultant) return undefined;
    return () => {
      // Land on the consultant home before the session changes so no auditor
      // route flashes under the consultant shell (docs/NAVIGATION.md section 1).
      navigate(CONSULTANT_HOME);
      onBackToConsultant();
    };
  }, [onBackToConsultant, navigate]);

  // The auditor model needs no counts: AppShell binds the badges itself.
  const model = useMemo<NavModel>(
    () => buildAuditorNav({ user, selectedOrgId, selectedClient, bureauName, onSwitchToConsultant: switchToConsultant, bureauKind: bureau?.kind ?? null }),
    [user, selectedOrgId, selectedClient, bureauName, switchToConsultant, bureau?.kind],
  );

  const adapter = useRouterLinkAdapter();

  const switches = useMemo<WorkspaceSwitch[]>(() => {
    if (!switchToConsultant) return [];
    return [{ id: "consultant", label: "Consultant workspace", description: "Leave the auditor portal", onSelect: switchToConsultant }];
  }, [switchToConsultant]);

  const paletteExtras = useCallback(async (): Promise<PaletteEntry[]> => {
    return clients.map((c) => ({
      id: `client:${c.id}`,
      label: c.name,
      hint: "Client",
      keywords: [c.sector ?? "", ...c.frameworkIds.map((fw) => FRAMEWORK_LABELS[fw] ?? fw)].filter(Boolean),
      to: auditClientPath(c.id),
    }));
  }, [clients]);

  const breadcrumb = selectedOrgId ? (
    <Breadcrumb
      items={[
        { label: "Clients", to: AUDITOR_HOME },
        { label: selectedClient?.name ?? "Client" },
        ...(activeTab ? [{ label: AUDIT_TAB_LABELS[activeTab] }] : []),
      ]}
    />
  ) : undefined;

  return (
    <AuditorClientsContext.Provider value={clientsState}>
      <AppShell
        model={model}
        adapter={adapter}
        user={user}
        topBar={{ breadcrumb, bell: "auditor" }}
        userMenu={{ switches, showExperienceMode: false, profileTo: "/auditor/account", versionTo: "/auditor/whats-new" }}
        paletteExtras={paletteExtras}
      >
        <Routes>
          <Route path="/auditor" element={<Navigate to={AUDITOR_HOME} replace />} />
          <Route path="/auditor/clients" element={<ClientListView user={user} />} />
          <Route path="/auditor/clients/:orgId" element={<EvidenceView onDetail={setDetailClient} readOnly={user.auditorRole === "observer"} />}>
            <Route index element={<AuditIndexRedirect />} />
            <Route path="evidence-map" element={<EvidenceMapTab />} />
            <Route path="controls" element={<ControlsFindingsTab />} />
            <Route path="registers" element={<AuditorRegistersTab />} />
            <Route path="snapshots" element={<SnapshotsRoute />} />
            <Route path="sampling" element={<SamplingRoute />} />
            <Route path="hardening" element={<HardeningTab />} />
            <Route path="technical-verification" element={<TechnicalVerificationTab />} />
            <Route path="submissions" element={<SubmissionsTab />} />
            <Route path="*" element={<AuditIndexRedirect />} />
          </Route>
          <Route path="/auditor/actions" element={<AuditorActionsView />} />
          <Route path="/auditor/analytics" element={<BureauAnalyticsView onSelectClient={(id) => navigate(auditClientPath(id))} />} />
          <Route path="/auditor/calendar" element={<AuditorCalendarPage />} />
          <Route path="/auditor/bureau/profile" element={<BureauProfileView user={user} />} />
          <Route path="/auditor/bureau/team" element={<RequireBureauAdmin user={user}><BureauTeamView currentUserId={user.id} /></RequireBureauAdmin>} />
          <Route path="/auditor/bureau/integrations" element={<RequireBureauAdmin user={user}><BureauConnectorsView /></RequireBureauAdmin>} />
          <Route path="/auditor/bureau/invitations" element={<RequireBureauAdmin user={user}><AuditorInvitationsPage /></RequireBureauAdmin>} />
          <Route path="/auditor/account" element={<ProfileView user={user} onNameChange={(name) => setUser((u) => ({ ...u, displayName: name }))} />} />
          <Route path="/auditor/whats-new" element={<WhatsNewPage />} />
          <Route path="*" element={<Navigate to={AUDITOR_HOME} replace />} />
        </Routes>
      </AppShell>
    </AuditorClientsContext.Provider>
  );
}

/** /auditor/clients/:orgId (and any unknown tab) opens the evidence map. */
function AuditIndexRedirect() {
  const { orgId = "" } = useParams<{ orgId: string }>();
  return <Navigate to={auditClientPath(orgId)} replace />;
}
